import { PROTECTED_TECHNICAL_TERMS } from '../glossary';
import { type TranslationTree } from '../types';

export const idTranslations: TranslationTree = {
  // Onboarding walkthrough copy. English source lives co-located in the
  // onboarding components/config (as t() fallbacks); these are the Indonesian
  // renderings. On-screen control names (buttons/tabs) stay in English because
  // the app chrome they point at is English.
  onboarding: {
    welcome: {
      title: 'Selamat datang di Qrema',
      intro:
        'Mari siapkan roastery-mu bersama. Dalam tujuh langkah singkat kamu akan menambah stok asli, lalu mencoba pembelian, roasting, cupping, dan penjualan, kemudian bertanya ke Qora soal angka-angkamu.',
      disclaimer:
        'Inventory-mu asli dan tetap tersimpan. Data latihan dihapus di akhir tur — tidak ada yang perlu dibatalkan.',
      skip: 'Lewati tur',
      start: 'Mulai tur',
    },
    coach: {
      stepLabel: 'Langkah {{step}} dari {{total}} · {{module}}',
      back: 'Kembali',
      skip: 'Lewati tur',
      working: 'Memproses…',
      next: 'Lanjut',
      finishTour: 'Selesaikan tur',
      navHint: 'Klik item yang disorot di menu →',
      guideHint: 'Gunakan kontrol yang disorot →',
      alreadyDoneHint: 'Langkah ini sudah kamu selesaikan — boleh diulang, atau klik Lanjut untuk meneruskan.',
    },
    fieldGuide: {
      fieldLabel: 'Kolom {{index}} dari {{total}} · {{label}}',
      looksGood: 'Sudah pas',
      backToHighlight: 'Kembali ke sorotan',
      fillToContinue: 'Isi dulu untuk lanjut',
      reviewAndCreate: 'Tinjau & buat',
      next: 'Lanjut',
      back: 'Kembali',
      skip: 'Lewati tur',
      useHighlightedButton: '↑ Gunakan tombol yang disorot',
    },
    completion: {
      confirmTitle: 'Selesaikan tur',
      doneTitle: 'Semua siap',
      confirmBody:
        'Kami akan menghapus pembelian, batch roasting, entri cupping, dan penjualan latihanmu. Inventory yang kamu masukkan di langkah 1 tetap persis seperti yang kamu tinggalkan.',
      notYet: 'Belum',
      cleaningUp: 'Membersihkan…',
      finishCleanUp: 'Selesai & bersihkan',
      error: 'Tidak bisa menyelesaikan tur. Coba lagi.',
      donePrompt: 'Apa yang mau kamu lakukan berikutnya?',
      buyBeans: 'Beli green bean',
      roast: 'Roasting stok yang ada',
      sell: 'Jual stok jadi',
    },
    steps: {
      '1': {
        module: 'Inventory',
        nav: 'Buka Inventory dari menu — di sinilah stok aslimu berada.',
        instruction: 'Klik "Add New Green Bean Item" untuk memasukkan item stok asli pertamamu.',
      },
      '2': {
        module: 'Purchasing',
        nav: 'Buka Purchasing, lalu Purchase Order, dari menu.',
        instruction:
          'Di sinilah kamu membeli green bean. Klik Create Purchase Order untuk membuat order latihan pertamamu.',
      },
      '3': {
        module: 'Production',
        nav: 'Buka Production, lalu Work Order, dari menu.',
        instruction:
          'Roasting terjadi di sini. Klik Create Work Order untuk me-roast batch latihan pertamamu.',
        hint: 'Sample roaster & mesin — tambahkan milikmu nanti di Settings → Roaster / Machine.',
      },
      '4': {
        module: 'Quality Control',
        nav: 'Buka Quality Control, lalu Cupping Session, dari menu.',
        instruction:
          'Nilai hasil roasting-mu di sini. Klik New Cupping Session untuk memulai cupping latihan pertamamu.',
        hint: 'Dua sample roasting sudah siap — pilih keduanya untuk dibandingkan dalam cupping.',
      },
      '5': {
        module: 'Finished Goods',
        nav: 'Buka Production, lalu Work Order, dari menu.',
        instruction:
          'Kemas hasil roasting-mu jadi produk yang bisa dijual di sini. Kami membuka tab Packing — pilih sample roasting-mu dan simpan untuk membuat finished good latihan.',
        hint: 'Packing mengubah roasted bulk menjadi finished good yang bisa kamu jual.',
      },
      '6': {
        module: 'Selling',
        nav: 'Buka Selling, lalu Sales Order, dari menu.',
        instruction:
          'Jual kopi jadi di sini. Buat penjualan latihan atas finished good yang baru kamu kemas, lalu jalankan dari Draft → Sent → Delivered.',
      },
      '7': {
        module: 'Report',
        nav: 'Buka Report, lalu General Report, dari menu.',
        instruction:
          'Inilah laporan yang dihasilkan rangkaian latihanmu — ganti tab untuk melihat roasting, inventory, penjualan, dan pelanggan. Ini pratinjau laporan aslimu nanti saat sudah live. Klik Lanjut.',
        hint: 'Pembelian, roasting, cupping, packing, dan penjualan latihanmu semua mengisi angka-angka ini.',
      },
      '8': {
        module: 'Qora',
        nav: 'Buka Qora Assistant dari menu.',
        instruction:
          'Tanya Qora soal inventory aslimu — misalnya berapa sisa suatu lot — lalu selesaikan turnya.',
      },
    },
    guides: {
      inventory: {
        banner: 'Ini inventory aslimu — masukkan nilai yang akurat. Data ini tetap tersimpan setelah tur.',
        reviewTitle: 'Tinjau, lalu buat',
        reviewBody:
          'Formulirmu kini terlihat penuh — pastikan setiap kolom benar, ini data asli. Kalau sudah pas, klik tombol Create yang disorot.',
        fields: {
          name: { label: 'Nama', instruction: 'Beri nama item stok ini dengan jelas — mis. "Ethiopia Yirgacheffe G1".' },
          species: { label: 'Species', instruction: 'Pilih species kopi (Arabica, Robusta…).' },
          process_type: { label: 'Jenis proses', instruction: 'Pilih cara beans ini diproses — Washed, Honey, atau Natural.' },
          quantity: {
            label: 'Kuantitas awal',
            instruction:
              'Masukkan berapa banyak stok yang benar-benar kamu miliki sekarang. Belum ada stok? Biarkan 0 — tidak masalah, kamu bisa menerima stok nanti.',
          },
          cost: { label: 'Biaya', instruction: 'Masukkan biaya pembelian asli — ini memengaruhi margin dan laporanmu nanti.' },
          reorder: {
            label: 'Peringatan reorder',
            instruction: 'Tetapkan level stok yang memicu peringatan stok menipis, agar kamu restock tepat waktu.',
          },
        },
      },
      workOrder: {
        banner: 'Ini roasting latihan memakai sample lot-mu — dihapus saat tur berakhir.',
        reviewTitle: 'Tinjau, lalu roasting',
        reviewBody: 'Periksa detail batch, lalu klik Save as Complete untuk me-roast batch latihan ini.',
        confirmInstruction: 'Konfirmasi untuk menyelesaikan roasting latihan.',
        fields: {
          machine: { label: 'Mesin', instruction: 'Pilih mesin roasting — kami sudah menyiapkan sample mesin untukmu.' },
          roaster: { label: 'Roaster', instruction: 'Pilih siapa yang me-roast — pilih sample roaster.' },
          date: { label: 'Tanggal', instruction: 'Tetapkan tanggal roasting.' },
          time: { label: 'Waktu', instruction: 'Tetapkan waktu roasting.' },
          save_draft: {
            label: 'Simpan dasar',
            instruction:
              'Klik Save as Draft untuk mengunci data dasar ini — itu membuka kolom bean, berat, dan roast type. Lalu klik Lanjut untuk meneruskan.',
          },
          green_bean: { label: 'Green bean', instruction: 'Pilih beans untuk di-roast — pilih sample green bean-mu.' },
          input_weight: { label: 'Input roasting', instruction: 'Masukkan berapa banyak yang kamu roasting (mis. 10 kg).' },
          output_weight: { label: 'Output roasting', instruction: 'Masukkan berat hasil roasting — sedikit lebih ringan dari input (mis. 8 kg).' },
          lots: { label: 'Tetapkan lot', instruction: 'Klik Auto-Calculate untuk mengambil dari sample lot-mu.' },
          roast_type: { label: 'Roast type', instruction: 'Pilih cara kamu me-roast beans ini.' },
        },
      },
      cupping: {
        banner: 'Ini cupping latihan memakai dua sample roasting-mu — dihapus saat tur berakhir.',
        reviewTitle: 'Tinjau, lalu simpan',
        reviewBody: 'Sample cupper-mu sudah terpilih. Klik Save Draft untuk mencatat cupping latihan ini.',
        confirmInstruction: 'Konfirmasi untuk mengunci mode cupping dan menyimpan cupping latihanmu.',
        fields: {
          roast_one: { label: 'Roasting pertama', instruction: 'Tambahkan sample roasting pertamamu — pilih dari daftar.' },
          roast_two: { label: 'Roasting kedua', instruction: 'Buka daftar lagi dan tambahkan sample roasting keduamu.' },
        },
      },
      cuppingScore: {
        banner: 'Sekarang nilai dua roasting latihanmu — geser tiap slider, lalu selesaikan cupping. Dihapus saat tur berakhir.',
        reviewTitle: 'Tinjau, lalu selesaikan',
        reviewBody: 'Kedua roasting sudah dinilai. Klik Complete Session untuk menyelesaikan cupping latihan ini.',
        confirmInstruction: 'Konfirmasi untuk menyelesaikan penilaian dan menuntaskan cupping latihan.',
        fields: {
          score_one: { label: 'Nilai roasting pertama', instruction: 'Geser slider Fragrance/Aroma untuk memberi nilai roasting pertama.' },
          open_second: { label: 'Buka roasting kedua', instruction: 'Klik tab roasting kedua untuk membukanya.' },
          score_two: { label: 'Nilai roasting kedua', instruction: 'Sekarang geser juga slider Fragrance/Aroma roasting ini — setiap roasting dalam sesi harus dinilai.' },
        },
      },
      fgOpen: {
        banner: 'Kamu sedang mengemas sample roasting-mu jadi produk yang bisa dijual — proses latihan yang dihapus saat tur berakhir.',
        reviewTitle: 'Buka formulir packing',
        reviewBody: 'Klik Packing Process untuk mulai mengemas sample roasting-mu.',
        fields: {
          switch_packing: {
            label: 'Beralih ke Packing',
            instruction: 'Roasting menghasilkan beans-mu; Packing mengubahnya jadi barang yang bisa dijual. Buka tab Packing.',
          },
          open_packing: { label: 'Mulai packing', instruction: 'Klik Packing Process untuk membuka formulir packing.' },
        },
      },
      fgCreate: {
        banner: 'Ini finished good latihan, dikemas dari sample roasting-mu — dihapus saat tur berakhir.',
        reviewTitle: 'Tinjau, lalu simpan',
        reviewBody: 'Periksa detail yang kamu masukkan dan roasting yang kamu pilih, lalu klik Save untuk mengemas finished good latihan ini.',
        fields: {
          name: { label: 'Nama', instruction: 'Ketik nama untuk finished good ini — mis. "House Blend 250g".' },
          pack_size: { label: 'Ukuran pack', instruction: 'Masukkan berat satu pack — mis. 0,25 kg.' },
          pick_roast: {
            label: 'Pilih roasting untuk dikemas',
            instruction: 'Pilih sample roasting-mu dari daftar — ini bulk yang kamu roasting sebelumnya.',
          },
          quantity: { label: 'Kuantitas', instruction: 'Masukkan berapa pack yang kamu buat — mis. 10.' },
          price: { label: 'Harga jual', instruction: 'Masukkan harga jual per pack — mis. Rp 100.000.' },
        },
      },
      fgComplete: {
        banner: 'Langkah terakhir — selesaikan produksi untuk memasukkan finished good ke stok. Dihapus saat tur berakhir.',
        reviewTitle: 'Selesaikan produksi',
        reviewBody: 'Semua terbawa dari draft-mu. Klik Complete untuk membuat stok yang bisa dijual.',
      },
      fgCompletePanel: {
        banner: 'Draft latihanmu tersimpan. Sekarang selesaikan untuk memasukkan finished good ke stok — dihapus saat tur berakhir.',
        reviewTitle: 'Selesaikan draft-mu',
        reviewBody: 'Klik Complete pada baris draft-mu untuk membuka langkah penyelesaian.',
      },
      fgView: {
        banner: 'Ini draft latihan yang kamu simpan — membukanya tidak mengubah apa pun. Dihapus saat tur berakhir.',
        reviewTitle: 'Draft tersimpanmu',
        reviewBody: 'Periksa angka yang kamu kemas, lalu tutup untuk kembali dan menyelesaikan draft-nya.',
      },
      salesOrderOpen: {
        banner: 'Kamu sedang menjual finished good yang baru kamu kemas — penjualan latihan yang dihapus saat tur berakhir.',
        reviewTitle: 'Buka formulir penjualan',
        reviewBody: 'Klik Create Sales Order untuk memulai penjualan latihanmu.',
        fields: {
          open_form: { label: 'Mulai penjualan', instruction: 'Klik Create Sales Order untuk membuka formulir penjualan.' },
        },
      },
      salesOrderCreate: {
        banner: 'Ini penjualan latihan atas finished good yang baru kamu kemas — dihapus saat tur berakhir.',
        reviewTitle: 'Tinjau, lalu simpan',
        reviewBody:
          'Periksa pelanggan, alamat pengiriman, finished good, kuantitas, harga, dan biaya pengiriman, lalu klik Save untuk mencatat sales order latihan ini sebagai draft.',
        fields: {
          customer: { label: 'Pelanggan', instruction: 'Pilih ke siapa kamu menjual — pilih sample customer dari daftar.' },
          shipping_address: {
            label: 'Alamat pengiriman',
            instruction: 'Masukkan tujuan pengiriman order — sample customer belum punya alamat tersimpan.',
          },
          pick_fg: { label: 'Pilih finished good', instruction: 'Pilih finished good yang baru kamu kemas — inilah yang kamu jual.' },
          qty: { label: 'Kuantitas', instruction: 'Masukkan berapa pack yang dijual — mis. 5.' },
          price: { label: 'Harga satuan', instruction: 'Masukkan harga jual per pack — mis. Rp 100.000.' },
          shipping_cost: {
            label: 'Biaya pengiriman',
            instruction: 'Masukkan biaya pengiriman order ini — biarkan Rp 0 jika tidak ada biaya kirim.',
          },
        },
      },
      salesOrderStatusRow: {
        banner: 'Sekarang jalankan order-mu melalui siklusnya: Draft → Sent → Delivered. Dihapus saat tur berakhir.',
        reviewTitle: 'Majukan order',
        reviewBody: 'Klik tombol status yang disorot untuk memindahkan order-mu ke tahap berikutnya — Sent, lalu Delivered.',
      },
      salesOrderStatusModal: {
        banner: 'Konfirmasi status baru untuk memajukan order latihanmu — dihapus saat tur berakhir.',
        reviewTitle: 'Konfirmasi status',
        reviewBody: 'Semua terbawa dari order. Klik Save untuk mengonfirmasi status barunya.',
      },
      purchaseOrder: {
        banner: 'Ini purchase order latihan memakai sample supplier-mu — dihapus saat tur berakhir.',
        reviewTitle: 'Tinjau, lalu buat',
        reviewBody:
          'Periksa supplier, item, dan kuantitas sudah benar, lalu klik tombol Create yang disorot untuk membuat order latihan ini.',
        fields: {
          supplier: { label: 'Supplier', instruction: 'Pilih supplier-mu — kami sudah menyiapkan sample supplier untuk kamu pakai.' },
          item: { label: 'Item', instruction: 'Pilih apa yang kamu beli — pilih sample green bean.' },
          quantity: { label: 'Kuantitas order', instruction: 'Masukkan berapa banyak yang kamu pesan (mis. 10 kg).' },
          delivery_status: {
            label: 'Status pengiriman',
            instruction: 'Tandai order ini sebagai Received agar beans masuk ke stokmu sebagai lot.',
          },
          arrival_weight: { label: 'Berat kedatangan', instruction: 'Masukkan berat yang benar-benar tiba.' },
          unit_price: { label: 'Harga satuan', instruction: 'Masukkan harga yang kamu bayar per kg.' },
          payment_type: { label: 'Jenis pembayaran', instruction: 'Pilih cara kamu membayar.' },
        },
      },
    },
  },
  common: {
    language: {
      label: 'Bahasa',
      english: 'English',
      indonesian: 'Bahasa Indonesia',
      code: {
        en: 'EN',
        id: 'ID',
      },
    },
    actions: {
      download: PROTECTED_TECHNICAL_TERMS.download,
      upload: PROTECTED_TECHNICAL_TERMS.upload,
      export: PROTECTED_TECHNICAL_TERMS.export,
      import: PROTECTED_TECHNICAL_TERMS.import,
      back: 'Kembali',
      logout: 'Keluar',
      collapse: 'Tutup',
      expandSidebar: 'Perluas Sidebar',
      collapseSidebar: 'Sembunyikan Sidebar',
      close: 'Tutup',
    },
    terms: {
      greenBean: PROTECTED_TECHNICAL_TERMS.greenBean,
      roastedBulk: PROTECTED_TECHNICAL_TERMS.roastedBulk,
      finishedGood: PROTECTED_TECHNICAL_TERMS.finishedGood,
    },
    user: {
      fallbackName: 'Pengguna',
    },
    header: {
      replayTour: 'Ulangi tur',
      replayTourError: 'Tidak bisa memulai ulang tur. Coba lagi.',
    },
    dateRange: {
      label: 'Rentang Tanggal',
      customRange: 'Rentang Kustom',
      thisWeek: 'Minggu Ini',
      thisMonth: 'Bulan Ini',
      lastMonth: 'Bulan Lalu',
      thisQuarter: 'Kuartal Ini',
      customSection: 'Rentang Kustom',
      startDate: 'Tanggal Mulai',
      endDate: 'Tanggal Selesai',
    },
    pagination: {
      prev: 'Sebelumnya',
      next: 'Berikutnya',
      page: 'Halaman',
      total: 'Total',
      label: 'Paginasi',
      showing: '{{to}} dari {{total}}',
      empty: 'Tidak ada hasil',
      pageOf: 'Halaman {{page}} dari {{totalPages}}',
      goToPage: 'Ke halaman {{page}}',
      perPage: 'Per halaman',
    },
    errorBoundary: {
      title: 'Terjadi kesalahan',
      unexpected: 'Terjadi kesalahan yang tidak terduga.',
      reload: 'Muat Ulang Halaman',
    },
    modal: {
      closeAriaLabel: 'Tutup dialog',
    },
    table: {
      na: 'N/A',
      unknown: 'Tidak diketahui',
      viewSupplier: 'Lihat Pemasok',
    },
    fileName: {
      roastProfilePrefix: 'RoastProfile',
    },
    statuses: {
      pending: 'menunggu',
    },
    view: 'Lihat',
    edit: 'Ubah',
    delete: 'Hapus',
    cancel: 'Batal',
    create: 'Buat',
    close: 'Tutup',
    save: 'Simpan',
    loading: 'Memuat…',
    search: 'Cari',
  },
  errors: {
    common: {
      unknownError: 'Error tidak diketahui',
      loadDataFailed: 'Gagal memuat data',
    },
    forbidden: {
      title: 'Tidak ada akses',
      inlineTitle: 'Bagian terbatas',
      description: 'Anda tidak memiliki izin untuk melihat konten ini. Hubungi administrator jika memerlukan akses.',
      inlineDescription: 'Anda tidak memiliki izin untuk melihat bagian ini.',
      pageTitle: 'Halaman ini terbatas',
      pageDescription: 'Role Anda tidak memiliki akses ke bagian ini. Pilih menu lain di sidebar, atau minta administrator memperbarui izin Anda.',
      backToDashboard: 'Kembali ke dashboard',
      goBack: 'Kembali',
    },
  },
  header: {
    language: {
      srLabel: 'Pemilih bahasa',
    },
  },
  sidebar: {
    dashboard: 'Dashboard',
    purchasing: 'Pembelian',
    purchaseOrder: 'Pesanan Pembelian',
    supplierMaster: 'Master Pemasok',
    lotList: 'Daftar Lot',
    sampleList: 'Daftar Sampel',
    inventory: 'Inventaris',
    production: 'Produksi',
    workOrder: 'Perintah Kerja',
    masterProfile: 'Profil Master',
    artisanSync: 'Sinkronisasi Artisan',
    qualityControl: 'Kontrol Kualitas',
    cuppingSession: 'Sesi Cupping',
    selling: 'Penjualan',
    salesOrder: 'Data Penjualan',
    customerMaster: 'Master Pelanggan',
    report: 'Laporan',
    generalReport: 'Laporan Umum',
    settings: 'Pengaturan',
    general: 'General',
    member: 'Member',
    adminMember: 'Admin Member',
    roastingMachine: 'Mesin Roasting',
    roastMaster: 'Master Roast',
    activityLogs: 'Log Aktivitas',
    logoAlt: 'Logo Qrema',
    subscription: 'Langganan',
    vouchers: 'Voucher',
    companies_subscriptions: 'Perusahaan & Langganan',
    adminConsole: 'Admin Console',
  },
  adminConsole: {
    badge: {
      title: 'Admin Console',
      subtitle: 'Kontrol level platform',
    },
    nav: {
      members: 'Admin Member',
      vouchers: 'Voucher',
      companies: 'Perusahaan & Langganan',
      lynkPayments: 'Riwayat Transaksi',
    },
    backToApp: 'Kembali ke aplikasi',
  },
  lynkPayments: {
    title: 'Riwayat Transaksi Lynk.id',
    subtitle: 'Log transaksi webhook Lynk.id (hanya lihat) untuk audit pembayaran.',
    searchPlaceholder: 'Cari email, message ID, ref ID, atau item...',
    empty: 'Tidak ada transaksi Lynk yang cocok dengan filter.',
    tierLabel: 'Tier',
    unknownTier: 'Tier tidak dikenali',
    noError: 'Tidak ada error',
    stats: {
      total: 'Total Transaksi',
      applied: 'Berhasil Diaplikasikan',
      failed: 'Gagal Diproses',
    },
    filters: {
      state: 'Status Aplikasi',
      all: 'Semua',
      applied: 'Berhasil',
      failed: 'Gagal',
    },
    status: {
      applied: 'Applied',
      failed: 'Failed',
    },
    columns: {
      receivedAt: 'Waktu Masuk',
      customerEmail: 'Email Customer',
      item: 'Item',
      amount: 'Nominal',
      status: 'Status',
      messageId: 'Message ID',
      refId: 'Ref ID',
      event: 'Event',
      company: 'Perusahaan',
      applyError: 'Apply Error',
    },
    detail: {
      title: 'Detail Event Lynk',
      rawPayload: 'Raw Payload',
    },
  },
  activityLogs: {
    title: 'Log Aktivitas',
    description: 'Jejak audit semua operasi create, update, dan delete di seluruh modul bisnis.',
    empty: 'Tidak ada aktivitas untuk filter ini.',
    loadError: 'Gagal memuat log aktivitas.',
    viewAll: 'Lihat semua',
    backToSettings: 'Kembali ke Pengaturan',
    filters: {
      module: 'Modul',
      allModules: 'Semua modul',
      startDate: 'Tanggal mulai',
      endDate: 'Tanggal akhir',
    },
    columns: {
      time: 'Waktu',
      module: 'Modul',
      type: 'Tipe',
      reference: 'Referensi',
      user: 'Pengguna',
    },
    types: {
      create: 'Dibuat',
      update: 'Diubah',
      delete: 'Dihapus',
    },
  },
  settings: {
    title: 'Pengaturan',
    subscription: 'Langganan',
    vouchers: 'Voucher',
    companies_subscriptions: 'Perusahaan & Langganan',
    personalPreferences: 'Preferensi Pribadi',
    personalPreferencesDescription: 'Kelola preferensi aplikasi lokal Anda.',
    defaultUnit: 'Satuan Ukur Default',
    defaultUnitPlaceholder: 'contoh: pcs, kg',
    theme: 'Tema',
    themeLight: 'Terang',
    themeLightDescription: 'Selalu gunakan tampilan terang',
    themeDark: 'Gelap',
    themeDarkDescription: 'Selalu gunakan tampilan gelap',
    themeAuto: 'Otomatis (Sistem)',
    themeAutoDescription: 'Ikuti preferensi perangkat Anda',
    themeCurrentStatus: 'Mode saat ini: {{mode}} · Tampilan aktif: {{appearance}}{{syncing}}',
    themeSyncingSuffix: ' · Menyinkronkan…',
    emailNotifications: {
      label: 'Notifikasi email',
      description: 'Jika aktif, pengingat work order juga akan dikirim ke inbox email kamu. Notifikasi web selalu aktif.',
      enabledToast: 'Notifikasi email diaktifkan',
      disabledToast: 'Notifikasi email dinonaktifkan',
      errorToast: 'Gagal memperbarui preferensi',
    },
    changePassword: {
      title: 'Ubah Kata Sandi',
      subtitle: 'Perbarui kata sandi akun Anda. Anda harus memasukkan kata sandi saat ini untuk konfirmasi.',
      currentPassword: 'Kata Sandi Saat Ini',
      newPassword: 'Kata Sandi Baru',
      confirmPassword: 'Konfirmasi Kata Sandi Baru',
      submit: 'Perbarui Kata Sandi',
      submitting: 'Memperbarui…',
      success: 'Kata sandi berhasil diperbarui',
      errors: {
        currentRequired: 'Kata sandi saat ini wajib diisi',
        newMinLength: 'Kata sandi baru minimal 6 karakter',
        confirmMismatch: 'Kata sandi tidak cocok',
        sameAsCurrent: 'Kata sandi baru harus berbeda dari kata sandi saat ini',
        currentIncorrect: 'Kata sandi saat ini salah',
        updateFailed: 'Gagal memperbarui kata sandi',
      },
    },
  },
  auth: {
    brandLogoAlt: 'Logo Qrema',
    toast: {
      welcomeBack: 'Selamat datang kembali!',
      passwordUpdated: 'Password berhasil diperbarui. Silakan masuk.',
    },
    validation: {
      invalidEmail: 'Masukkan alamat email yang valid',
      passwordRequired: 'Password wajib diisi',
      companyNameShort: 'Nama perusahaan terlalu pendek',
      fullNameRequired: 'Masukkan nama lengkap Anda',
      phoneMinLength: 'Nomor HP minimal 4 digit',
      passwordMinLength: 'Password minimal 6 karakter',
      passwordUppercase: 'Password harus mengandung minimal satu huruf kapital',
      passwordMismatch: 'Konfirmasi password tidak cocok',
      emailAlreadyInUse: 'Email ini sudah digunakan',
      otpLength: 'OTP harus 6 digit',
      confirmPasswordMinLength: 'Konfirmasi password minimal 6 karakter',
      resetTokenInvalid: 'Token reset tidak ada atau tidak valid',
      invalidEmailSimple: 'Alamat email tidak valid',
    },
    login: {
      title: 'Masuk ke akun Anda',
      newHere: 'Baru di sini?',
      registerCompany: 'Daftarkan perusahaan Anda',
      emailAddress: 'Alamat email',
      emailPlaceholder: 'nama@perusahaan.com',
      password: 'Password',
      forgotPassword: 'Lupa password?',
      passwordPlaceholder: '••••••••',
      submit: 'Masuk',
    },
    register: {
      title: 'Buat akun',
      alreadyHaveCompany: 'Sudah punya perusahaan?',
      signInHere: 'Masuk di sini',
      companyName: 'Nama Perusahaan',
      fullName: 'Nama Lengkap',
      emailAddress: 'Alamat Email',
      companyNamePlaceholder: 'PT Maju Jaya',
      fullNamePlaceholder: 'Budi Santoso',
      emailPlaceholder: 'budi@majujaya.com',
      phoneNumber: 'Nomor HP',
      phoneNumberPlaceholder: '812 3456 7890',
      password: 'Password',
      confirm: 'Konfirmasi',
      next: 'Lanjut',
      voucherCode: 'Kode Voucher',
      voucherCodeOptional: 'Kode Voucher (opsional)',
      voucherCodePlaceholder: 'XXXX-XXXX-XXXX',
      voucherCodeHint: 'Punya voucher? Dapatkan tambahan bulan di atas masa percobaan gratis Anda.',
      trialBadgeTitle: 'Termasuk uji coba Pro gratis 1 bulan',
      trialBadgeSubtitle: 'Register sekarang untuk uji coba Pro selama 1 bulan.',
      voucherInvalid: 'Kode voucher tidak valid.',
      voucherExpired: 'Voucher ini sudah kedaluwarsa.',
      voucherExhausted: 'Voucher ini telah mencapai batas penggunaan.',
      voucherNotFound: 'Kode voucher tidak ditemukan.',
    },
    forgotPassword: {
      title: 'Lupa password?',
      subtitle: 'Masukkan email Anda dan kami akan mengirimkan tautan reset',
      success: 'Jika akun untuk {{email}} tersedia, tautan reset telah dikirim.',
      emailAddress: 'Alamat email',
      submit: 'Kirim',
      submitting: 'Mengirim',
      rememberedPassword: 'Sudah ingat password?',
      signIn: 'Masuk',
    },
    resetPassword: {
      title: 'Reset password Anda',
      subtitle: 'Masukkan password baru untuk akun Anda',
      newPassword: 'Password baru',
      confirmNewPassword: 'Konfirmasi password baru',
      updatePassword: 'Perbarui password',
      backToSignIn: 'Kembali ke halaman masuk',
    },
    verifyOtp: {
      title: 'Verifikasi email Anda',
      subtitle: 'Kami mengirim kode verifikasi ke {{email}}',
      verificationCode: 'Kode Verifikasi',
      submit: 'Verifikasi & Daftar',
      resendPrompt: 'Belum menerima kode?',
      resend: 'Kirim ulang kode',
      resendIn: 'Kirim ulang dalam {{seconds}}d',
      resending: 'Mengirim...',
      resendSuccess: 'Kode baru telah dikirim ke email Anda.',
      resendCooldown: 'Mohon tunggu {{seconds}} detik sebelum meminta kode baru.',
    },
    common: {
      success: 'Berhasil!',
      failed: 'Gagal',
      confirm: 'Konfirmasi',
      cancel: 'Batal',
      closeModal: 'Tutup modal',
    },
  },
  dashboard: {
    sessionExpired: 'Sesi berakhir. Silakan login kembali.',
    syncThemeWarning: 'Gagal menyinkronkan preferensi tema. Akan dicoba ulang otomatis.',
    syncLanguageWarning: 'Gagal menyinkronkan preferensi bahasa. Akan dicoba ulang otomatis.',
    syncPreferencesWarning: 'Gagal menyinkronkan preferensi dashboard.',
    page: {
      title: 'Dashboard',
      subtitle: 'Ringkasan metrik bisnis Anda',
      syncingPreferences: 'Menyinkronkan preferensi dashboard...',
      dashboardMenu: 'Menu Dashboard',
      connectionError: 'Kesalahan Koneksi',
      errorOccurred: 'Terjadi Kesalahan',
      modalCreateSalesOrder: 'Buat Sales Order',
      modalCreatePurchaseOrder: 'Buat Purchase Order',
      modalCreateWorkOrder: 'Buat Work Order',
      createSalesOrderError: 'Gagal membuat sales order',
      createPurchaseOrderError: 'Gagal membuat purchase order',
      createWorkOrderError: 'Gagal membuat work order',
      supplierNotFound: 'Pemasok tidak ditemukan.',
      supplierLoadError: 'Gagal memuat detail pemasok.',
    },
    greenBeanInfographics: {
      error: 'Gagal memuat infografis species dan varietas green bean.',
      empty: 'Tidak ada profil green bean untuk ditampilkan.',
      title: 'Species & Varietas Green Bean',
      subtitle: 'Distribusi species dan varietas',
      species: 'Species',
      varietals: 'Varietas',
      profileCount: '{{count}} profil',
    },
    greenBeanLotsModal: {
      title: `${PROTECTED_TECHNICAL_TERMS.greenBean} Lots`,
      total: 'Total',
      prev: 'Sebelumnya',
      next: 'Berikutnya',
      page: 'Halaman',
      loading: 'Memuat...',
      errorLoad: 'Gagal memuat green bean lots.',
      empty: 'Tidak ada green bean lots.',
      viewSupplier: 'Lihat Pemasok',
      noSupplierSelected: 'Tidak ada pemasok dipilih.',
      supplierNotFound: 'Pemasok tidak ditemukan.',
      supplierLoadFailed: 'Gagal memuat detail pemasok.',
      columns: {
        lot: 'Lot',
        lastPurchaseKg: 'Pembelian Terakhir (kg)',
        remainingKg: 'Sisa (kg)',
        netPrice: 'Harga Bersih',
      },
    },
    quickActions: {
      title: 'Quick Actions',
      createSO: 'Buat SO',
      salesOrder: 'Sales Order',
      createPO: 'Buat PO',
      purchaseOrder: 'Purchase Order',
      createWO: 'Buat WO',
      workOrder: 'Work Order',
      addProduct: 'Tambah Produk',
      inventory: 'Inventaris',
    },
    activityFeed: {
      title: 'Aktivitas Terbaru',
      systemActivity: 'Aktivitas Sistem',
      salesOrder: 'Sales Order',
      purchaseOrder: 'Purchase Order',
      workOrder: 'Work Order',
      stockMovement: 'Pergerakan Stok',
    },
    averageShrinkage: {
      title: 'Rata-rata Shrinkage',
      trendIncreased: 'Meningkat dari periode sebelumnya',
      trendDecreased: 'Menurun dari periode sebelumnya',
      trendStable: 'Stabil dari periode sebelumnya',
      footnote: 'Dihitung dari work order roasting yang selesai.',
    },
    financialStats: {
      revenue: 'Pendapatan',
      expenses: 'Pengeluaran',
      netProfit: 'Laba Bersih',
      outstanding: 'Tertunda',
      greenBean: PROTECTED_TECHNICAL_TERMS.greenBean,
      roasted: PROTECTED_TECHNICAL_TERMS.roastedBulk,
      finished: PROTECTED_TECHNICAL_TERMS.finishedGood,
      outstandingPending: '{{count}} menunggu',
      totalPrefix: 'Total:',
      financialOverview: 'Ringkasan Keuangan',
      inventoryValue: 'Nilai Inventori',
    },
    inventoryAlerts: {
      expiringSoon: 'Segera Kedaluwarsa',
      lowStock: 'Stok Menipis',
      lot: 'Lot',
      batch: 'Batch',
      expiry: 'Kedaluwarsa',
      daysSuffix: 'hari',
      noExpiringItems: 'Tidak ada item yang segera kedaluwarsa.',
      noLowStockItems: 'Tidak ada item dengan stok menipis.',
      reorder: 'Pesan Ulang',
      restock: 'Restock',
      min: 'Min',
    },
    menu: {
      customizeTitle: 'Kustomisasi Dashboard',
      description: 'Pilih bagian yang ingin ditampilkan pada dashboard utama Anda.',
      mainSections: 'Bagian Utama',
      quickStats: 'Statistik Cepat',
      activeCount: '{{count}} dari {{total}} aktif',
      cancel: 'Batal',
      applyChanges: 'Terapkan Perubahan',
      groupFinancialStats: 'Statistik Keuangan',
      groupGreenBeanProfile: 'Spesies & Varietas Green Bean',
      itemAiAssistant: 'Asisten AI',
      itemActivityFeed: 'Aktivitas Terbaru',
      itemExpiringSoon: 'Segera Kedaluwarsa',
      itemLowStock: 'Stok Menipis',
      itemAverageShrinkage: 'Rata-rata Shrinkage',
      itemQuickActions: 'Aksi Cepat',
      cardRevenue: 'Pendapatan',
      cardExpenses: 'Pengeluaran',
      cardNetProfit: 'Laba Bersih',
      cardOutstandingPayment: 'Pembayaran Tertunda',
      cardGreenBean: PROTECTED_TECHNICAL_TERMS.greenBean,
      cardRoasted: PROTECTED_TECHNICAL_TERMS.roastedBulk,
      cardFinished: PROTECTED_TECHNICAL_TERMS.finishedGood,
      cardSpecies: 'Spesies',
      cardVarietals: 'Varietas',
    },
    expensesModal: {
      title: 'Pengeluaran',
      loading: 'Memuat detail pengeluaran...',
      error: 'Gagal memuat pengeluaran purchase order.',
      empty: 'Tidak ada purchase order pada rentang tanggal ini.',
      poNumber: 'Nomor PO',
      buyWeight: 'Berat Beli',
      arrivalWeight: 'Berat Tiba',
      yieldShrinkage: 'Yield Shrinkage',
      unitPrice: 'Harga Satuan',
      extraExpense: 'Biaya Tambahan',
      totalCost: 'Total Biaya',
    },
    roastedBulkModal: {
      title: `Stok ${PROTECTED_TECHNICAL_TERMS.roastedBulk}`,
      total: 'Total',
      loading: 'Memuat...',
      error: `Gagal memuat stok ${PROTECTED_TECHNICAL_TERMS.roastedBulk}.`,
      empty: `Tidak ada stok ${PROTECTED_TECHNICAL_TERMS.roastedBulk}.`,
      item: 'Item',
      roastType: 'Tipe Roast',
      totalStockKg: 'Total Stok (kg)',
    },
    outstandingPaymentsModal: {
      title: 'Pembayaran Tertunda',
      loading: 'Memuat pembayaran tertunda...',
      empty: 'Tidak ada pembayaran tertunda.',
      orderNumber: 'Nomor Order',
      supplier: 'Pemasok',
      date: 'Tanggal',
      total: 'Total',
      orderStatus: 'Status Order',
      paymentStatus: 'Status Pembayaran',
      deliveryStatus: 'Status Pengiriman',
      actions: 'Aksi',
      markAsPaid: 'Tandai Lunas',
      markAsPaidTitle: 'Tandai Lunas - {{orderNumber}}',
    },
    supplierModal: {
      titleWithName: 'Pemasok: {{name}}',
      title: 'Pemasok',
      loading: 'Memuat pemasok...',
      noSelected: 'Tidak ada pemasok dipilih.',
    },
    greenBeanProfile: {
      unknown: 'Tidak diketahui',
      others: 'Lainnya',
      profileCount: '{{count}} profil',
      error: 'Gagal memuat infografis spesies dan varietas green bean.',
      empty: 'Tidak ada profil green bean untuk ditampilkan.',
      title: 'Spesies & Varietas Green Bean',
      subtitle: 'Distribusi spesies dan varietas',
      species: 'Spesies',
      varietals: 'Varietas',
    },
  },
  production: {
    machineForm: {
      addMachine: 'Tambah Mesin',
      addType: 'Tambah Tipe',
      machine: 'Mesin',
      typeName: 'Nama Tipe',
      sampleRoasterMachine: 'Mesin Sampel Roaster',
      minimumInput: 'Input Minimum',
      maximumInput: 'Input Maksimum',
      sampleCapacityHint: 'Kapasitas mesin sampel roaster harus antara 50g dan 300g.',
      notesPlaceholder: 'Tambahkan observasi internal atau riwayat perawatan...',
      createMachine: 'Buat Mesin',
      saveType: 'Simpan Tipe',
      createType: 'Buat Tipe',
      errors: {
        minGreater: 'Input minimum tidak boleh lebih besar dari input maksimum',
        minMaxRequired: 'Input minimum dan maksimum wajib diisi untuk Mesin Sampel Roaster.',
        sampleCapacityRange: 'Silakan masukkan kapasitas mesin sampel roaster antara 50g dan 300g.',
      },
      toast: {
        typeUpdated: 'Tipe berhasil diperbarui.',
        typeCreated: 'Tipe berhasil dibuat.',
        typeSaveFailed: 'Gagal menyimpan tipe.',
      },
    },
    roasterList: {
      title: 'Master Roaster',
      searchPlaceholder: 'Cari berdasarkan nama...',
      loading: 'Memuat roaster...',
      empty: 'Tidak ada roaster.',
      confirmDelete: 'Apakah Anda yakin?',
      actions: {
        add: 'Tambah Roaster',
      },
      modal: {
        addTitle: 'Tambah Roaster',
        editTitle: 'Ubah Roaster',
      },
      toast: {
        created: 'Roaster berhasil dibuat.',
        updated: 'Roaster berhasil diperbarui.',
        deleted: 'Roaster berhasil dihapus.',
        saveFailed: 'Gagal menyimpan roaster.',
        deleteFailed: 'Gagal menghapus roaster.',
        duplicateName: 'Nama ini sudah ada. Gunakan nama yang berbeda.',
      },
    },
    roasterForm: {
      notesPlaceholder: 'Tambahkan catatan internal untuk roaster ini...',
      createRoaster: 'Buat Roaster',
    },
    machineList: {
      title: 'Mesin Roasting',
      loading: 'Memuat mesin...',
      empty: 'Tidak ada mesin.',
      emptyTypes: 'Tidak ada tipe untuk mesin ini.',
      confirmDeleteMachine: 'Apakah Anda yakin ingin menghapus mesin ini?',
      confirmDeleteType: 'Apakah Anda yakin ingin menghapus tipe mesin ini?',
      actions: {
        addMachineType: 'Tambah Mesin / Tipe',
        editMachine: 'Ubah Mesin',
        deleteMachine: 'Hapus Mesin',
        editType: 'Ubah Tipe',
        deleteType: 'Hapus Tipe',
      },
      modal: {
        addMachineType: 'Tambah Mesin / Tipe',
        editMachine: 'Ubah Mesin',
        editType: 'Ubah Tipe',
      },
      table: {
        machineName: 'Nama Mesin',
        sampleRoaster: 'Sampel Roaster',
        actions: 'Aksi',
        typeName: 'Nama Tipe',
        status: 'Status',
        minInput: 'Input Minimum',
        maxInput: 'Input Maksimum',
      },
      values: {
        yes: 'Ya',
        no: 'Tidak',
        active: 'Aktif',
        inactive: 'Tidak Aktif',
      },
      toast: {
        machineDeleted: 'Mesin berhasil dihapus.',
        machineDeleteFailed: 'Gagal menghapus mesin.',
        typeDeleted: 'Tipe berhasil dihapus.',
        typeDeleteFailed: 'Gagal menghapus tipe.',
        machineUpdated: 'Mesin berhasil diperbarui.',
        machineCreated: 'Mesin berhasil dibuat.',
        machineSaveFailed: 'Gagal menyimpan mesin.',
      },
    },
  },
  purchasing: {
    sampleList: {
      title: 'Daftar Sampel',
      loading: 'Memuat sampel...',
      empty: 'Tidak ada sampel.',
      filters: {
        search: 'Cari',
        searchPlaceholder: 'Cari berdasarkan No Sampel, Nama Item, atau Kode',
        date: 'Tanggal',
      },
      table: {
        sampleNumber: 'Nomor Sampel',
        item: 'Item',
        notes: 'Catatan',
        source: 'Sumber',
        greenBeanProfile: 'Profil Green Bean',
        roastProfile: 'Profil Roast',
        tasteProfile: 'Profil Cupping',
        receivedDate: 'Tanggal Diterima',
        qualityControl: 'Kontrol Kualitas',
        supplier: 'Pemasok',
      },
      values: {
        unknownItem: 'Item Tidak Diketahui',
        fillProfileInLot: 'Isi profil di lot',
      },
      pagination: {
        showing: 'Menampilkan {{from}} sampai {{to}} dari {{total}} hasil',
      },
      modal: {
        greenBeanQc: 'QC Green Bean',
        cuppingQc: 'QC Cupping',
      },
    },
    lotList: {
      title: 'Lot Diterima',
      loading: 'Memuat lot...',
      empty: 'Tidak ada lot.',
      filters: {
        search: 'Cari',
        searchPlaceholder: 'Cari berdasarkan No Lot atau Item',
        status: 'Status',
      },
      status: {
        all: 'Semua',
        active: 'Aktif',
        depleted: 'Habis',
      },
      table: {
        lotNumber: 'Nomor Lot',
        item: 'Item',
        source: 'Sumber',
        receivedDate: 'Tanggal Diterima',
        remaining: 'Sisa',
        status: 'Status',
        greenBeanProfile: 'Profil Green Bean',
        roastProfile: 'Profil Roast',
        tasteProfile: 'Profil Cupping',
        qualityControl: 'Kontrol Kualitas',
        actions: 'Aksi',
      },
      values: {
        unknownItem: 'Item Tidak Diketahui',
      },
      actions: {
        view: 'Lihat',
        viewPhoto: 'Lihat Foto',
      },
      modal: {
        greenBeanQc: 'QC Green Bean',
        cuppingQc: 'QC Cupping',
        lotPhoto: 'Foto Lot',
        workOrderReadOnly: 'Work Order — {{number}}',
      },
    },
    supplierList: {
      title: 'Master Pemasok',
      loading: 'Memuat pemasok...',
      empty: 'Tidak ada pemasok.',
      confirmDelete: 'Apakah Anda yakin?',
      filters: {
        search: 'Cari',
        searchPlaceholder: 'Cari berdasarkan nama, email, catatan...',
        status: 'Status',
      },
      status: {
        all: 'Semua',
        active: 'Aktif',
        inactive: 'Tidak Aktif',
      },
      table: {
        name: 'Nama',
        email: 'Email',
        phone: 'Telepon',
        status: 'Status',
        internalNotes: 'Catatan Internal',
        actions: 'Aksi',
      },
      actions: {
        add: 'Tambah Pemasok',
        edit: 'Ubah',
        delete: 'Hapus',
        openWhatsApp: 'Buka WhatsApp',
        call: 'Telepon',
      },
      modal: {
        addTitle: 'Tambah Pemasok',
        editTitle: 'Ubah Pemasok',
      },
      toast: {
        deleteFailed: 'Gagal menghapus pemasok.',
        saveFailed: 'Gagal menyimpan pemasok.',
        inUse: 'Pemasok ini tidak bisa dihapus karena sedang digunakan.',
      },
    },
  },
  purchaseOrder: {
    actions: {
      edit: 'Ubah',
      viewProof: 'Lihat Bukti Pembayaran',
      downloadProof: 'Download Bukti Pembayaran',
    },
    table: {
      title: 'Purchase Order',
      loading: 'Memuat purchase order...',
      errorLoad: 'Gagal memuat purchase order.',
      empty: 'Tidak ada purchase order untuk supplier ini.',
      orderStatus: 'Status Order',
      paymentStatus: 'Status Pembayaran',
      deliveryStatus: 'Status Pengiriman',
      lineItemsLots: 'Item Baris & Lot',
      lotTrackingActive: 'Pelacakan Lot Aktif',
      qtyOrdered: 'Qty Dipesan',
      qtyReceived: 'Qty Diterima',
      processing: 'Memproses...',
      pendingReceipt: 'Menunggu Penerimaan',
      viewGreenBeanProfile: 'Lihat Profil Green Bean',
      addGreenBeanProfile: 'Tambah Profil Green Bean',
      orderNumberShort: 'Order #',
      shrinkageKg: 'Shrinkage (kg)',
      shrinkagePercent: 'Shrinkage (%)',
      lotId: 'Lot ID',
      unitPrice: 'Harga Satuan',
      total: 'Total',
    },
    filters: {
      search: 'Cari',
      searchPlaceholder: 'Kode PO atau pemasok...',
      order: 'Order',
      payment: 'Pembayaran',
      delivery: 'Pengiriman',
      dates: 'Tanggal',
      dateRangeFilters: 'Filter Rentang Tanggal',
      orderDate: 'Tanggal Order',
      deliveryDate: 'Tanggal Pengiriman',
      applyFilters: 'Terapkan Filter',
      options: {
        all: 'Semua',
        draft: 'Draft',
        ordered: 'Dipesan',
        cancelled: 'Dibatalkan',
        unpaid: 'Belum Dibayar',
        paid: 'Sudah Dibayar',
        refunded: 'Dikembalikan',
        pending: 'Pending',
        shipping: 'Shipping',
        received: 'Received',
        bankTransfer: 'Transfer Bank',
        cod: 'Bayar di Tempat',
      },
    },
    paymentDetails: {
      title: 'Detail Pembayaran',
      paymentStatus: 'Status Pembayaran',
      paymentType: 'Tipe Pembayaran',
      proofOfPayment: 'Bukti Pembayaran',
      other: 'Lainnya',
      typePaymentMethod: 'Ketik metode pembayaran',
      attachFile: 'Lampirkan file',
      removeAttachment: 'Hapus lampiran',
      uploadFailed: 'Upload gagal',
      downloadFailed: 'Gagal mengunduh lampiran',
      acceptedFormats: 'Diterima: jpg, jpeg, png, pdf. Maks. ukuran file: 1 MB.',
      invalidFormat: 'Format file tidak didukung. Diizinkan: jpg, jpeg, png, pdf.',
      fileTooLarge: 'Ukuran file melebihi batas maksimum 1 MB.',
    },
    items: {
      addItem: 'Tambah Item',
      selectItem: 'Pilih Item',
      weight: 'Berat',
      arrival: 'Berat Tiba',
      sampleItem: 'Sample Item',
      unitPriceGreaterThanZero: '*Harga satuan harus lebih besar dari 0',
      arrivalExceedsBuyWeight: '*Berat tiba tidak boleh melebihi berat beli',
    },
    deliveryDetails: {
      title: 'Detail Pengiriman',
      actualDeliveryDate: 'Tanggal Pengiriman Aktual',
      deliveryStatus: 'Status Pengiriman',
    },
    form: {
      submit: {
        create: 'Buat Purchase Order',
        save: 'Simpan Purchase Order',
        receive: 'Diterima',
        previewPo: 'Preview',
        downloadPo: 'Unduh PO',
        preparing: 'Menyiapkan...',
      },
      fields: {
        supplier: 'Pemasok',
        supplierPlaceholder: 'Pilih atau ketik pemasok baru',
        supplierInternalNote: 'Catatan Internal Pemasok',
        supplierInternalNotePlaceholder: 'Catatan internal untuk pemasok...',
        orderDate: 'Tanggal Order',
        orderStatus: 'Status Order',
        internalNotes: 'Catatan Internal',
        internalNotesPlaceholder: 'Catatan internal...',
      },
      generalInformation: 'Informasi Umum',
      errors: {
        supplierInactive: 'Pemasok yang dipilih tidak aktif.',
        supplierUnresolved: 'Silakan pilih pemasok dari daftar.',
        actualDeliveryDateInvalid: 'Tanggal Actual Delivery harus sama atau setelah Tanggal Order',
        arrivalWeightRequired: 'Mohon isi Arrival Weight untuk semua item.',
      },
      toast: {
        supplierInactive: 'Pemasok yang dipilih tidak aktif dan tidak dapat dipakai untuk purchase order baru.',
        supplierUnresolved: 'Pemasok tersebut tidak dikenali. Silakan pilih ulang dari daftar.',
        receivedSuccess: 'Purchase Order diterima. Inventory Lots dan Samples berhasil dibuat.',
        savedSuccess: 'Purchase Order berhasil disimpan.',
        saveFailed: 'Gagal menyimpan Purchase Order. Silakan coba lagi.',
      },
      values: {
        itemWithCode: 'Item {{code}}',
        unknownItem: 'Item Tidak Diketahui',
        lossDescription: 'Losses dari PO {{itemName}} ({{weightDifference}} kg × {{unitPrice}})',
      },
      modals: {
        highShrinkageTitle: 'Peringatan Shrinkage Tinggi',
        highShrinkageWarning: 'Peringatan: Item berikut memiliki shrinkage lebih dari 30%:',
        highShrinkageItem: '{{name}}: {{shrinkage}}% shrinkage',
        highShrinkageConfirm: 'Apakah Anda yakin ingin melanjutkan?',
        unitPriceAdjustmentTitle: 'Penyesuaian Harga Satuan',
        proceed: 'Lanjutkan',
        weightDiscrepancyTitle: 'Perbedaan Berat Terdeteksi',
        weightDiscrepancyDescription: 'Berat diterima berbeda dari order. Apakah Anda ingin menghitung ulang harga satuan menggunakan:',
        totalDivReceivedWeight: 'Total ÷ Berat Diterima',
        weightComparison: 'Perbandingan Berat',
        ordered: 'Dipesan',
        received: 'Diterima',
        priceAdjustment: 'Penyesuaian Harga',
        keepOriginal: 'Tetap Original',
        applyAdjustments: 'Terapkan Penyesuaian',
      },
    },
  },
  salesOrder: {
    payment: {
      title: 'Catat Pembayaran',
      markAsPaid: 'Tandai Lunas',
      edit: 'Ubah pembayaran',
      save: 'Simpan Pembayaran',
      orderTotal: 'Total Pesanan',
      statusLabel: 'Status Pembayaran',
      typeLabel: 'Metode Pembayaran',
      refundNote: 'Menandai pesanan ini sebagai refund akan menghapus pendapatannya dari laporan bisnis, pelanggan, dan dashboard.',
      status: {
        paid: 'Lunas',
        unpaid: 'Belum Bayar',
        refunded: 'Refund',
      },
      types: {
        bankTransfer: 'Transfer Bank',
        cash: 'Tunai',
        other: 'Lainnya',
        placeholder: 'Ketik metode pembayaran',
      },
      proof: {
        label: 'Bukti pembayaran',
        attach: 'Lampirkan file',
        uploading: 'Mengunggah…',
        accepted: 'PDF, JPG, atau PNG — maks 5 MB',
        remove: 'Hapus lampiran',
        invalidFormat: 'Format file tidak valid. Gunakan PDF, JPG, atau PNG.',
        fileTooLarge: 'File terlalu besar. Maks 5 MB.',
        uploadFailed: 'Gagal mengunggah. Coba lagi.',
        downloadFailed: 'Gagal mengunduh. Coba lagi.',
      },
      toast: {
        saved: 'Pembayaran diperbarui.',
        failed: 'Gagal memperbarui pembayaran.',
      },
    },
    formHeader: {
      customer: 'Pelanggan',
      customerPlaceholder: 'Pilih atau ketik pelanggan baru',
      customerEmail: 'Email pelanggan',
      orderDate: 'Tanggal order',
      terms: 'Termin',
      selectTerms: 'Pilih termin',
      net7: 'Net 7',
      net14: 'Net 14',
      net30: 'Net 30',
      dueOnReceipt: 'Jatuh tempo saat diterima',
      invoiceDate: 'Tanggal invoice',
      dueDate: 'Tanggal jatuh tempo',
      balanceDue: 'SISA TAGIHAN',
      invoiceNo: 'No. invoice',
      store: 'Toko',
    },
    formDetails: {
      billingAddress: 'Alamat penagihan',
      shippingTo: 'Alamat pengiriman',
      sameAsBilling: 'Sama dengan alamat penagihan',
      expectedShipDate: 'Perkiraan tanggal kirim',
      shipVia: 'Kirim via',
      shippingDate: 'Tanggal pengiriman',
      trackingNo: 'No. resi',
      sales: 'Sales',
      tags: 'Tag',
      tagsPlaceholder: 'Mulai ketik untuk menambahkan tag',
    },
    items: {
      items: 'Item',
      clearAllLines: 'Hapus semua baris',
      addLines: 'Tambah baris',
      productService: 'Produk/Layanan',
      selectProductService: 'Pilih Produk/Layanan',
      remaining: 'Tersisa',
      rate: 'Harga ({{currencySymbol}})',
      ratePerKg: 'Harga ({{currencySymbol}}/kg)',
      qtyKg: 'Qty (kg)',
      qtyG: 'Qty (g)',
      itemType: 'Tipe Item',
      unitPriceGreaterThanZero: '*Harga satuan harus lebih besar dari 0',
      subtotal: 'Subtotal',
      shipping: 'Pengiriman',
      discount: 'Diskon',
      balanceDue: 'Sisa tagihan',
      errors: {
        quantityExceedsRemainingLimit: 'Kuantitas melebihi batas sisa',
      },
      lotPicker: {
        label: 'Lot',
        fifoAuto: 'FIFO (otomatis)',
        fifoHint: 'Pakai lot tertua terlebih dahulu',
        manualHeader: 'Pilih lot tertentu',
        noProcessType: 'Tanpa process type',
        unknownLot: 'Lot tidak diketahui',
        noLots: 'Tidak ada lot aktif',
      },
    },
    footer: {
      messageOnInvoice: 'Pesan pada invoice',
      internalNotesPlaceholder: 'Catatan internal...',
      preparing: 'Menyiapkan...',
      previewInvoice: 'Preview',
      downloadInvoice: 'Unduh Invoice',
      updateOrder: 'Perbarui Order',
      createOrder: 'Buat Order',
      confirmCancellationTitle: 'Konfirmasi Pembatalan',
      confirmCancellationMessage: 'Peringatan: Membatalkan order yang sudah delivered akan membalik transaksi keuangan. Apakah Anda yakin ingin melanjutkan?',
      confirmCancelOrder: 'Ya, Batalkan Order',
    },
    list: {
      title: 'Sales Order',
      loading: 'Memuat sales order...',
      errorLoad: 'Gagal memuat sales order',
      newButton: 'Sales Order Baru',
      filterStatus: 'Filter Status:',
      filters: {
        search: 'Pencarian',
        searchPlaceholder: 'Cari no. order atau pelanggan',
        dates: 'Tanggal',
        dateRangeFilters: 'Filter Rentang Tanggal',
        orderDate: 'Tanggal Order',
        applyFilters: 'Terapkan Filter',
      },
      totalAmount: 'Total Nilai',
      markAsSent: 'Tandai Terkirim',
      markAsDelivered: 'Tandai Terkirim Selesai',
      empty: 'Tidak ada sales order.',
      status: {
        all: 'Semua',
        draft: 'Draft',
        sent: 'Terkirim',
        delivered: 'Selesai Dikirim',
        cancelled: 'Dibatalkan',
      },
      table: {
        orderNumber: 'No Order',
        customer: 'Pelanggan',
        date: 'Tanggal',
        total: 'Total',
        deliveryStatus: 'Status Pengiriman',
        paymentStatus: 'Status Pembayaran',
        status: 'Status',
        internalNotes: 'Catatan Internal',
      },
      modal: {
        createTitle: 'Buat Sales Order',
        editTitle: 'Ubah {{orderNumber}}',
        customerDetails: 'Detail Pelanggan',
      },
    },
    service: {
      customerNotFoundCreateFirst: 'Pelanggan tidak ditemukan. Silakan buat pelanggan terlebih dahulu (Email dan Alamat Penagihan wajib diisi).',
    },
  },
  selling: {
    customerList: {
      title: 'Master Pelanggan',
      loading: 'Memuat pelanggan...',
      empty: 'Tidak ada pelanggan.',
      confirmDelete: 'Apakah Anda yakin?',
      table: {
        name: 'Nama',
        email: 'Email',
        phone: 'Telepon',
        billingAddress: 'Alamat Penagihan',
        shippingAddress: 'Alamat Pengiriman',
        messageOnInvoice: 'Pesan di Invoice',
        internalNotes: 'Catatan Internal',
        actions: 'Aksi',
      },
      actions: {
        add: 'Tambah Pelanggan',
        edit: 'Ubah',
        delete: 'Hapus',
        openWhatsApp: 'Buka WhatsApp',
        call: 'Telepon',
      },
      modal: {
        addTitle: 'Tambah Pelanggan',
        editTitle: 'Ubah Pelanggan',
      },
      toast: {
        created: 'Pelanggan berhasil dibuat.',
        updated: 'Pelanggan berhasil diperbarui.',
        deleted: 'Pelanggan berhasil dihapus.',
        saveFailed: 'Gagal menyimpan pelanggan.',
        deleteFailed: 'Gagal menghapus pelanggan.',
        inUse: 'Pelanggan ini tidak bisa dihapus karena sedang digunakan.',
      },
    },
  },
  workOrders: {
    viewOtherWorkOrders: 'Lihat riwayat work order lainnya',
    historyList: {
      title: 'Pilih work order',
    },
    common: {
      areYouSure: 'Apakah Anda yakin?',
      cancelling: 'Membatalkan...',
      deleting: 'Menghapus...',
    },
    bulk: {
      modeLabel: 'Mode pembuatan',
      modeSingle: 'Satuan',
      modeBulk: 'Bulk',
      scheduleTitle: 'Jadwal {{index}}',
      addSchedule: 'Tambah Jadwal Baru',
      removeSchedule: 'Hapus jadwal ini',
      maxReached: 'Maksimal {{max}} jadwal dalam satu kali pembuatan.',
      draftBadge: 'Draft',
      savedBadge: 'Tersimpan',
      savingDrafts: 'Menyimpan draft...',
      scheduling: 'Menjadwalkan...',
      fixFieldsFirst: 'Ada jadwal dengan isian wajib yang belum lengkap. Perbaiki lalu coba lagi.',
      createFailed: 'Jadwal {{index}} gagal: {{message}}. {{created}} draft sudah dibuat — tekan Save as Draft lagi untuk mengulang sisanya.',
      incompleteHint: 'Pilih green bean, minimal satu lot beserta beratnya, dan tipe roast untuk menjadwalkan work order ini.',
      lotBudgetHint: 'Sisa stok sudah dikurangi lot yang dipakai jadwal lain dalam batch ini.',
      confirmTitle: 'Jadwalkan work order',
      confirmBody: '{{ready}} jadwal akan dijadwalkan. {{pending}} jadwal belum lengkap dan akan tetap berstatus draft.',
      confirmPendingList: 'Tetap draft:',
      confirmContinue: 'Lanjutkan',
      scheduleResult: '{{scheduled}} work order dijadwalkan, {{draft}} tetap draft',
      scheduleFailed: 'Gagal menjadwalkan {{code}}: {{message}}',
      missingFieldsBody: 'Lengkapi field wajib sebelum menjadwalkan. Belum ada data yang disimpan.',
      issues: {
        bean: 'Green bean',
        lots: 'Minimal satu lot beserta beratnya',
        roastType: 'Tipe roast',
        insufficientStock: 'Berat lot melebihi stok tersisa',
        outputExceedsInput: 'Roast Output melebihi Roast Input',
      },
    },
    page: {
      title: 'Work Order',
      loading: 'Memuat work order...',
      newWorkOrder: 'Work Order Baru',
      packingProcess: 'Proses Packing',
      mode: 'Mode',
      roasting: 'Roasting',
      packing: 'Packing',
      emptyFiltered: 'Tidak ada work order yang cocok dengan filter.',
      errorCancel: 'Gagal membatalkan work order. Silakan coba lagi.',
      errorDelete: 'Gagal menghapus work order. Silakan coba lagi.',
      viewWorkOrder: 'Lihat Work Order',
      updateWorkOrder: 'Ubah Work Order',
      moveToInventory: 'Pindah ke Inventory (Roasted Goods)',
      errors: {
        loadDetailsFailed: 'Gagal memuat detail work order',
        selectAtLeastOneLot: 'Tidak dapat memindahkan work order ke scheduled/completed. Pilih minimal satu lot.',
        updateFailed: 'Gagal memperbarui work order',
        completeFailed: 'Gagal menyelesaikan work order',
        onlyCancelledDelete: 'Hanya work order yang dibatalkan yang dapat dihapus.',
        saveFinishedGoodFailed: 'Gagal menyimpan finished good',
      },
      success: {
        roastedBulkUpdated: 'Roasted Bulk berhasil diperbarui!',
        roastedBulkCreated: 'Roasted Bulk berhasil dibuat!',
      },
    },
    filters: {
      search: 'Cari',
      searchPlaceholder: 'Cari berdasarkan nomor work order, nama recipe, nama item, kode item...',
      status: 'Status',
      roastType: 'Tipe Roast',
      machine: 'Mesin',
      machineType: 'Tipe Mesin',
      allStatuses: 'Semua Status',
      allRoastTypes: 'Semua Tipe Roast',
      allMachines: 'Semua Mesin',
      allMachineTypes: 'Semua Tipe Mesin',
      toggleFilters: 'Filter',
      activeCount: '{{count}} aktif',
      selectedCount: '{{count}} dipilih',
    },
    table: {
      workOrder: 'Work Order',
      item: 'Item',
      roastType: 'Tipe Roast',
      roastInput: 'Roast Input (kg)',
      roastOutput: 'Roast Output (kg)',
      shrinkage: 'Shrinkage',
      orderDate: 'Tanggal Order',
      actualEnd: 'Akhir Aktual',
      status: 'Status',
      actions: 'Aksi',
    },
    cards: {
      input: 'Input',
      output: 'Output',
      order: 'Order',
      target: 'Target',
      na: 'N/A',
    },
    actions: {
      start: 'Mulai',
      complete: 'Selesaikan',
      cancel: 'Batalkan',
      delete: 'Hapus',
      stockIn: 'Stock In',
      updateStock: 'Perbarui Stok',
      editStock: 'Ubah Stok',
      editFinishedGoods: `Ubah ${PROTECTED_TECHNICAL_TERMS.finishedGood}`,
      createFinishedGoods: `Buat ${PROTECTED_TECHNICAL_TERMS.finishedGood}`,
      downloadProfile: 'Unduh Profil',
      saveRecipe: 'Simpan Resep',
      savingDraft: 'Menyimpan Draft...',
      saveAsDraft: 'Simpan sebagai Draft',
      saveAsScheduled: 'Simpan sebagai Terjadwal',
      saveAsComplete: 'Simpan sebagai Selesai',
      stopwatchInProgressHint: 'Selesaikan stopwatch (atau reset) sebelum menyimpan sebagai terjadwal atau selesai.',
      onboardingOutputRequiredHint: 'Isi berat output roasting untuk menyelesaikan roasting latihan ini — tur hanya lanjut setelah work order selesai.',
    },
    status: {
      draft: 'Draft',
      scheduled: 'Terjadwal',
      in_progress: 'Sedang Proses',
      completed: 'Selesai',
      cancelled: 'Dibatalkan',
    },
    fgProductions: {
      title: 'Draft & Proses Produksi',
      subtitle: 'Buat draft, selesaikan proses packing, dan tinjau riwayat.',
      searchPlaceholder: 'Cari kode packing, nama item, kode item',
      loading: 'Memuat produksi...',
      empty: 'Tidak ada data produksi yang cocok dengan filter.',
      errorLoad: 'Gagal memuat produksi finished goods. Silakan coba lagi.',
      table: {
        packingCode: 'Kode Packing',
        finishedGood: PROTECTED_TECHNICAL_TERMS.finishedGood,
        qty: 'Qty',
        status: 'Status',
        created: 'Dibuat',
        actions: 'Aksi',
      },
      actions: {
        complete: 'Selesaikan',
        view: 'Lihat',
        cancel: 'Batalkan',
      },
      status: {
        all: 'Semua Status',
        draft: 'Draft',
        completed: 'Selesai',
        cancelled: 'Dibatalkan',
      },
      cancelModal: {
        title: 'Batalkan Draft Produksi',
        description: 'Ini akan membatalkan draft dan Anda mungkin kehilangan data produksi yang belum selesai.',
        errorCancel: 'Gagal membatalkan draft produksi. Silakan coba lagi.',
        confirm: 'Ya, batalkan draft',
      },
    },
    waste: {
      title: `${PROTECTED_TECHNICAL_TERMS.roastedBulk} Waste Management`,
      processing: 'Memproses...',
      empty: 'Tidak ada komponen untuk diproses.',
      manualLabel: 'Manual',
      manualAdjustmentNote: 'Penyesuaian sisa manual (keep): {{from}} -> {{to}} kg',
      wasteNote: 'Waste roasted bulk setelah produksi finished goods',
      errorNoLot: 'Tidak ada lot untuk {{itemName}} ({{roastType}})',
      errorStockChanged: 'Stok sisa berubah. Silakan buka ulang waste management untuk {{itemName}}.',
      errorProcess: 'Gagal memproses waste management. Silakan coba lagi.',
      table: {
        item: 'Item',
        remainingKg: 'Sisa (kg)',
        action: 'Aksi',
      },
      actions: {
        keep: 'Keep',
        waste: 'Waste',
        confirm: 'Konfirmasi',
        confirmAdjust: 'Konfirmasi & Sesuaikan',
      },
      confirmManualTitle: 'Konfirmasi Sisa Manual',
      confirmManualHeading: 'Ada nilai sisa yang diubah secara manual',
      confirmManualDescription:
        'Item di bawah ditandai keep, tetapi nilai sisanya diubah manual. Ini akan membuat penyesuaian stok berdasarkan nilai manual.',
    },
    cancelModal: {
      title: 'Batalkan Work Order',
      scheduledDescription:
        'Membatalkan work order terjadwal akan mengembalikan stok roast input dan mencatat pergerakan stok pembalikan.',
      defaultDescription: 'Membatalkan work order ini akan menandainya sebagai dibatalkan dan menghentikan proses lanjutan.',
      confirm: 'Ya, batalkan work order',
    },
    deleteModal: {
      title: 'Hapus Work Order Dibatalkan',
      description: 'Ini akan menghapus permanen work order yang dibatalkan dan tidak bisa dibatalkan kembali.',
      confirm: 'Ya, hapus work order',
    },
    closeEditConfirmModal: {
      title: 'Tutup Form Work Order',
      description: 'Perubahan yang belum disimpan pada work order ini akan hilang. Apakah Anda ingin melanjutkan?',
      keepEditing: 'Lanjut mengedit',
      discard: 'Buang perubahan',
    },
    roastTypeEdit: {
      saveButton: 'Simpan Tipe Roast',
      warningTitle: 'Edit Tipe Roast pada Work Order yang Selesai',
      warningMessage:
        'Anda akan mengubah tipe roast pada work order yang sudah selesai. Mohon tinjau sebelum mengkonfirmasi.',
      scopeHeader: 'Cakupan perubahan',
      scopeOnlyRoastType: 'Hanya tipe roast yang akan diperbarui (header, snapshot, dan saved recipe jika ada).',
      scopeStockUnchanged: 'Stok yang sudah dikurangi di inventory TIDAK akan dikembalikan.',
      backToEdit: 'Kembali edit',
      confirm: 'Ya, perbarui tipe roast',
      saving: 'Memperbarui...',
      savedToast: 'Tipe roast diperbarui',
      saveFailed: 'Gagal memperbarui tipe roast.',
    },
    completeSummaryModal: {
      title: 'Konfirmasi Selesaikan Roast',
      banner: 'Tinjau sebelum diselesaikan',
      headline: 'Siap menyelesaikan roast ini?',
      notice: 'Setelah diselesaikan, stok akan dikurangi dan work order ini akan dikunci.',
      section01: 'Bean & Lot',
      section02: 'Input → Output',
      section03: 'Agtron Meter',
      section04: 'Tipe Roast',
      section05: 'Tanggal Kedaluwarsa',
      noBean: 'Belum ada bean dipilih',
      noLots: 'Tidak ada lot tercatat',
      total: 'Total terpakai',
      input: 'Input',
      output: 'Output',
      shrinkage: 'Penyusutan',
      wholeBean: 'Whole',
      groundBean: 'Ground',
      noAgtron: 'Belum tercatat',
      noRoastType: 'Belum ditentukan',
      expiryOptional: 'Opsional',
      expiryClear: 'Hapus',
      expiryHelp: 'Diterapkan pada stok hasil roast yang dibuat dari work order ini.',
      confirm: 'Konfirmasi & Selesaikan',
      confirming: 'Menyelesaikan...',
      cancel: 'Kembali edit',
    },
    beanSelection: {
      title: 'Pemilihan Bean',
      selectGreenBean: 'Pilih green bean...',
      autoCalculate: 'Hitung Otomatis',
      calculating: 'Menghitung...',
      optional: '(opsional)',
      selectRoastType: 'Pilih tipe roast',
      noLotsTitle: 'Green bean ini belum memiliki lot tersedia',
      noLotsHint: 'Terima stok melalui Purchase Order sebelum menjadwalkan roast untuk bean ini.',
      roast: {
        sample: 'Sample Roast',
        filter: 'Filter Roast',
        omni: 'Omni Roast',
        espresso: 'Espresso Roast',
      },
    },
    roastProfile: {
      title: 'Profil Roast',
      useRoastProfile: 'Gunakan Profil Roast',
      useStopwatchMode: 'Gunakan Mode Stopwatch',
      roastingReference: 'Referensi Roasting',
      artisanProfile: 'Profil Artisan',
      useArtisanProfile: 'Gunakan Profil Artisan',
      linkArtisanProfile: 'Tautkan Profil Artisan',
      noDate: 'Tanpa tanggal',
      untitled: 'Tanpa judul',
      selectUnsyncedProfile: 'Pilih profil yang belum sinkron...',
      clearArtisanProfile: 'Hapus profil artisan',
      roastProfiles: 'Profil Roast',
      noType: 'Tanpa tipe',
      selectProfile: 'Pilih profil...',
      selectGreenBeanFirst: 'Pilih green bean terlebih dahulu...',
      newProfile: 'Profil Baru',
      saveAsNewProfile: 'Simpan sebagai profil baru',
      snapshotProfile: 'Snapshot Profile',
    },
    modals: {
      totalUsedWeightMismatch: 'Total Berat Terpakai Tidak Sesuai',
      totalUsedWeightMessage: 'Total berat terpakai dari lot berbeda dengan Roast Input.',
      totalUsedWeight: 'Total Berat Terpakai',
      adjustInputOrLots: 'Sesuaikan Roast Input atau kuantitas per lot agar sesuai sebelum melanjutkan.',
      roastOutputExceedsInput: 'Roast Output Melebihi Input',
      outputCannotExceedInput: 'Roast Output tidak boleh lebih besar dari Roast Input.',
      adjustOutput: 'Sesuaikan Roast Output agar sesuai sebelum melanjutkan.',
      warning: 'Peringatan',
      back: 'Kembali',
      ok: 'OK',
    },
    form: {
      loading: 'Memuat...',
      saved: 'Tersimpan ✓',
      workOrderCode: 'Kode Work Order',
      selectOrTypeMachine: 'Pilih atau ketik mesin',
      selectOrTypeRoaster: 'Pilih atau ketik roaster',
      minInput: 'Input Min',
      maxInput: 'Input Maks',
      addMachineType: 'Tambah Tipe Mesin',
      addRoaster: 'Tambah Roaster',
      unknownLot: 'Lot Tidak Diketahui',
      used: 'Terpakai',
      unknownRecipe: 'Resep Tidak Diketahui',
      unlinkModal: {
        title: 'Lepas Kaitan Profil Artisan',
        message: 'Lepas kaitan Profil Artisan dari Work Order ini? Relasi roast profile Artisan pada Work Order akan dilepas.',
        no: 'Tidak',
        unlinking: 'Melepas...',
        yesUnlink: 'Ya, lepas',
      },
      toast: {
        machineCreated: 'Mesin berhasil dibuat.',
        machineCreateFailed: 'Gagal membuat mesin.',
        roasterCreated: 'Roaster berhasil dibuat.',
        roasterCreateFailed: 'Gagal membuat roaster.',
        machineTypeCreated: 'Tipe mesin berhasil dibuat.',
        artisanUnlinked: 'Profil Artisan berhasil dilepas.',
        artisanUnlinkFailed: 'Gagal melepas profil Artisan.',
      },
      errors: {
        outputExceedsInput: 'Roast output tidak boleh lebih besar dari roast input.',
        loadArtisanProfileDetailsFailed: 'Gagal memuat detail profil Artisan.',
        autoCalculateLotsFailed: 'Gagal menghitung otomatis lot',
        allocationErrorOccurred: 'Terjadi kesalahan saat alokasi.',
        roastTypeRequiredWhenUsingProfile: 'Tipe roast wajib diisi saat menggunakan roast profile.',
        roastTypeRequired: 'Tipe roast wajib diisi.',
        selectAtLeastOneLotBeforeScheduling: 'Silakan pilih minimal satu lot sebelum menjadwalkan work order ini.',
        selectAtLeastOneLotWithQuantityIfBeanSelected: 'Silakan pilih minimal satu lot dengan kuantitas jika bean dipilih.',
        lotsExceedRemainingStock: 'Satu atau lebih lot melebihi stok tersisa. Silakan sesuaikan kuantitas.',
        createFailedCheckInputs: 'Gagal membuat work order. Silakan periksa input.',
      },
    },
  },
  inventory: {
    import: {
      buttonLabel: 'Impor Inventaris Green Bean',
      modalTitle: 'Impor Inventaris Green Bean',
      step1Title: 'Unggah',
      step2Title: 'Tinjau & Edit',
      step3Title: 'Selesai',
      downloadTemplate: 'Unduh template',
      uploadHint: 'Letakkan file .xlsx di sini atau klik untuk memilih',
      uploadParsing: 'Memproses…',
      reviewSummary: '{{rows}} baris · {{errors}} error',
      submit: 'Impor',
      cancel: 'Batal',
      back: 'Kembali',
      done: 'OK',
      successToast: '{{count}} baris green bean berhasil diimpor',
      errors: {
        notXlsx: 'File harus berupa spreadsheet .xlsx',
        tooLarge: 'File terlalu besar (maks 5 MB)',
        tooManyRows: 'Terlalu banyak baris (maks 500)',
        missingHeader: 'Kolom wajib pada spreadsheet tidak ditemukan',
        serverError: 'Impor gagal — silakan coba lagi',
      },
    },
    importRoastedBulk: {
      buttonLabel: 'Impor Roasted Bulk',
      modalTitle: 'Impor Roasted Bulk',
      step1Title: 'Pilih green bean',
      step2Title: 'Unggah',
      step3Title: 'Tinjau',
      step4Title: 'Selesai',
      errors: {
        serverError: 'Impor gagal — silakan periksa baris dan coba lagi',
      },
    },
    modal: {
      titleCreate: 'Buat Item Baru',
      titleEdit: 'Ubah Item',
      errorSave: 'Gagal menyimpan item: {{message}}',
      errorFixFields: 'Mohon perbaiki field yang ditandai lalu coba lagi.',
      errorSummaryTitle: 'Perbaiki {{count}} isian sebelum menyimpan',
      errorSummaryTitleOne: 'Perbaiki 1 isian sebelum menyimpan',
      errorSummarySubtitle: 'Klik salah satu item di bawah untuk langsung ke field yang perlu diperbaiki.',
      sections: {
        basicInformation: 'Informasi Dasar',
        greenBeanExtrinsic: `Ekstrinsik ${PROTECTED_TECHNICAL_TERMS.greenBean}`,
        stockCost: 'Stok & Biaya',
        sellingPackaging: 'Penjualan & Kemasan',
      },
      fields: {
        code: 'Kode',
        codeAuto: 'Otomatis',
        name: 'Nama',
        status: 'Status',
        description: 'Deskripsi',
        itemType: 'Tipe Item',
        location: 'Lokasi',
        locationPlaceholder: 'contoh: Gudang 1',
        producer: 'Nama Produsen',
        producerPlaceholder: 'Pilih atau tambah produsen',
        species: 'Spesies',
        speciesPlaceholder: 'contoh: Arabica',
        variety: 'Varietas',
        varietyPlaceholder: 'contoh: Bourbon',
        processType: 'Tipe Proses',
        processTypePlaceholder: 'Isi tipe proses',
        quantity: 'Kuantitas',
        initialQuantity: 'Kuantitas Awal',
        standardCost: 'Biaya Standar',
        reorderWarning: 'Peringatan Reorder',
        reorderQuantity: 'Kuantitas Reorder',
        estimatedSellingPrice: 'Estimasi Harga Jual',
        packSize: 'Ukuran Kemasan',
        packSizePlaceholder: 'contoh: 0.5',
      },
      loadingItemDetails: 'Memuat detail item...',
      status: {
        active: 'Aktif',
        inactive: 'Tidak Aktif',
      },
      validation: {
        max100: 'Maksimal 100 karakter',
      },
      actions: {
        saving: 'Menyimpan...',
        creating: 'Membuat...',
        saveChanges: 'Simpan Perubahan',
        createItem: 'Buat Item',
        close: 'Tutup',
      },
      warning: {
        title: 'Peringatan',
        inactiveBlocked: `Tidak bisa mengubah status menjadi tidak aktif karena ${PROTECTED_TECHNICAL_TERMS.greenBean} ini masih memiliki lot aktif.`,
        ok: 'OK',
      },
    },
    lotDetail: {
      loading: 'Memuat detail lot...',
      notFound: 'Lot tidak ditemukan',
      uploading: 'Mengunggah...',
      loadingShort: 'Memuat...',
      titleWithNumber: 'Lot {{lotNumber}}',
      uploadHint: 'PNG, JPG, atau WEBP hingga 2MB',
      emptyMovements: 'Tidak ada pergerakan.',
      status: {
        active: 'Aktif',
        depleted: 'Habis',
      },
      sections: {
        lotDetails: 'Detail Lot',
        inventoryStatus: 'Status Inventory',
        stockMovementHistory: 'Riwayat Pergerakan Stok',
        profiles: 'Profil',
      },
      fields: {
        item: 'Item',
        receivedDate: 'Tanggal Diterima',
        expiryDate: 'Tanggal Kedaluwarsa',
        source: 'Sumber',
        linkedSample: 'Sample Terkait',
        supplier: 'Supplier',
        unitPrice: 'Harga Satuan',
        initialQuantity: 'Kuantitas Awal',
        remainingQuantity: 'Kuantitas Tersisa',
        greenBeanPhoto: 'Foto Green Bean',
        photoUploadedAt: 'Diunggah pada',
        quantityChange: 'Perubahan Kuantitas',
        user: 'Pengguna',
        photo: 'Foto',
      },
      values: {
        na: 'N/A',
        unknownItem: 'Item Tidak Diketahui',
      },
      actions: {
        viewSample: 'Lihat Sample',
        changePhoto: 'Ubah Foto',
        clickUpload: 'Klik untuk unggah foto',
        removePhoto: 'Hapus foto',
      },
      errors: {
        uploadFailed: 'Gagal mengunggah gambar',
        fileTooLarge: 'Ukuran file harus kurang dari 2MB',
        invalidFileType: 'Hanya PNG, JPG, atau WEBP yang diperbolehkan',
      },
    },
    roastedBulk: {
      loadingLots: 'Memuat lot roasted...',
      emptyLots: 'Tidak ada lot roasted untuk grup ini.',
      empty: 'Tidak ada lot roasted yang cocok dengan filter Anda.',
      woPrefix: 'WO',
      remaining: 'Sisa',
      in: 'Masuk',
      out: 'Keluar',
      shrink: 'Shrink',
      expiry: 'Kedaluwarsa',
      history: 'Riwayat',
      totalStockKg: 'Total Stok (kg)',
      averagePrice: 'Harga Rata-rata',
      unknownRoastType: 'Tipe Roast Tidak Diketahui',
      actions: 'Aksi',
      adjust: {
        button: 'Kurangi Stok',
        title: 'Kurangi Stok',
        subtitle: 'Stok diambil dari lot terlama lebih dulu (FIFO).',
        availableLabel: 'Tersedia',
        afterLabel: 'Setelah pengurangan',
        quantityLabel: 'Jumlah',
        reasonLabel: 'Alasan',
        reasonPlaceholder: 'Pilih alasan...',
        reasons: {
          waste: 'Rusak / Terbuang',
          sample: 'Sampel / Cupping',
          internal: 'Pemakaian Internal',
          correction: 'Koreksi Stok',
        },
        notesLabel: 'Catatan',
        notesPlaceholder: 'Apa yang terjadi?',
        previewTitle: 'Lot yang akan diambil',
        previewEmpty: 'Masukkan jumlah untuk melihat lot mana yang dipakai.',
        previewTotal: 'Total biaya',
        columns: {
          workOrder: 'Work Order',
          quantity: 'Diambil',
          unitCost: 'Biaya / kg',
          cost: 'Biaya',
        },
        lotEmptied: 'Lot habis',
        submit: 'Kurangi Stok',
        submitting: 'Memproses...',
        cancel: 'Batal',
        errors: {
          quantityRequired: 'Masukkan jumlah lebih dari nol.',
          quantityTooLarge: 'Hanya tersedia {{available}} pada grup ini.',
          reasonRequired: 'Pilih alasan.',
          notesRequired: 'Catatan wajib diisi.',
          failed: 'Gagal mengurangi stok.',
        },
        success: 'Mengurangi {{quantity}} ({{cost}}) dari {{item}}.',
      },
      columns: {
        workOrder: 'Work Order',
        inputKg: 'Input (kg)',
        outputKg: 'Output (kg)',
        shrinkage: 'Shrinkage',
        remaining: 'Sisa',
        expiry: 'Kedaluwarsa',
        cogs: 'Harga Pokok Penjualan',
        beanProfile: 'Profil Bean',
        roastProfile: 'Profil Roast',
        cuppingProfile: 'Profil Cupping',
      },
    },
    finishedGoods: {
      componentsTitle: `Komponen ${PROTECTED_TECHNICAL_TERMS.roastedBulk}`,
      empty: 'Belum ada komponen. Gunakan "Tambah Komponen" untuk memulai.',
      emptyInventory: `Tidak ada item ${PROTECTED_TECHNICAL_TERMS.finishedGood} yang cocok dengan filter Anda.`,
      summary: {
        title: 'Ringkasan Produksi',
        detailCalculation: 'Detail Perhitungan',
        totalInput: 'Total Input',
        weightPerPcs: 'Berat / Pcs',
        miscellaneousCost: 'Biaya Tambahan',
        packaging: 'Kemasan',
        estimatedSellingPricePerPcs: 'Estimasi Harga Jual / Pcs',
        currencyPlaceholder: 'Rp 0',
        unitPlaceholder: '1',
        totalCogs: 'Total HPP',
        unitCostPerPcs: 'Biaya Unit / Pcs',
      },
      placeholders: {
        selectRoastedBulk: 'Pilih roasted bulk...',
      },
      actions: {
        add: 'Tambah',
        remove: 'Hapus',
        addComponent: 'Tambah Komponen',
        removeComponent: 'Hapus Komponen',
      },
      columns: {
        roastedBulkItem: 'Item Roasted Bulk',
        roastType: 'Tipe Roast',
        availableKg: 'Tersedia (kg)',
        allocation: 'Alokasi',
        finalAllocation: 'Alokasi Akhir',
      },
      errors: {
        allocationExceeds: 'Alokasi melebihi pack size',
        totalAllocationPrefix: 'Total alokasi',
        totalAllocationSuffix: 'tidak boleh melebihi pack size',
        totalInputVsTarget: 'Total Input vs Target',
        totalInputWeightPrefix: 'Total berat input',
        totalInputWeightSuffix: 'tidak sesuai target',
        stockExceeded: 'STOK TERLALU BESAR',
      },
      finalProduct: {
        title: 'Produk Akhir',
        packSize: 'Pack Size',
        selectFinishedGoods: 'Pilih finished goods...',
        enterNewName: 'Masukkan nama finished goods baru',
        packSizePlaceholder: '0.500',
        required: 'Produk akhir wajib dipilih.',
        packSizeRequired: 'Pack size wajib diisi.',
        nameAlreadyExists: 'Nama finished good ini sudah digunakan.',
      },
    },
    sampleModal: {
      title: 'Tambah Sampel',
      errors: {
        dateRequired: 'Tanggal wajib diisi',
        createFailed: 'Gagal membuat sampel',
      },
    },
    samples: {
      empty: 'Tidak ada sampel aktif.',
      actions: {
        addSample: 'Tambah Sampel',
      },
    sampleQA: {
      notChecked: 'Belum Dicek',
      passed: 'Lolos',
      failed: 'Gagal',
    },
    },
    basicInfo: {
      title: 'Informasi Dasar',
      generatedBySystem: 'Dibuat oleh sistem',
      unitOfMeasure: 'Satuan Ukur',
      units: {
        kg: 'kg',
        g: 'g',
        lb: 'lb',
        oz: 'oz',
        pcs: 'pcs',
        bag: 'bag',
      },
    },
    createItem: {
      cogsBatch: 'Cost of Goods Sold Batch ({{currencySymbol}})',
      roastInputFromWorkOrder: 'Roast Input dari Work Order',
      roastOutput: 'Roast Output',
      updatesHistory: '(Memperbarui Riwayat)',
      quantityPlaceholder: 'contoh: 5.2',
      workOrderIdOptional: 'Work Order ID (Opsional)',
      expiryDateOptional: 'Tanggal Kedaluwarsa (Opsional)',
      create: 'Buat',
      update: 'Perbarui',
      errors: {
        max100Chars: 'Maksimal 100 karakter',
        outputGreaterThanZero: 'Roast output harus lebih besar dari 0',
        outputExceedsInput: 'Roast output tidak boleh melebihi roast input',
      },
      shrinkageModal: {
        title: 'Peringatan Shrinkage Tinggi',
        warningPrefix: 'Peringatan: Shrinkage adalah',
        comparisonDetails: 'Detail Perbandingan:',
        input: 'Input',
        output: 'Output',
        confirm: 'Shrinkage di atas 25%. Apakah data input dan output sudah benar?',
      },
    },
    createPage: {
      profileCode: 'Kode Profil',
      profileName: 'Nama Profil',
    },
    page: {
      confirmDeleteItem: 'Apakah Anda yakin ingin menghapus item ini?',
      failedDeleteItemPrefix: 'Gagal menghapus item: ',
    },
    filters: {
      searchNameOrCode: 'Cari berdasarkan nama atau kode...',
    },
    header: {
      addNewGreenBeanItem: 'Tambah Item Green Bean Baru',
    },
    typeFilter: {
      itemType: 'Tipe Item',
      greenBeans: 'Green Beans',
      roastedBulks: 'Roasted Bulks',
      finishedGoods: 'Finished Goods',
    },
    modals: {
      confirmUpdateData: 'Apakah Anda yakin ingin memperbarui data?',
      selectTasteProfile: 'Pilih Profil Rasa',
      itemHasTasteProfiles: 'Item ini memiliki {{count}} profil rasa. Pilih salah satu untuk dilihat:',
      finishedGoodsTasteProfile: 'Profil Rasa Finished Goods',
      loadTasteProfilesFailed: 'Gagal memuat profil rasa',
      tasteProfileTitlePrefix: 'Profil Rasa',
      roastProfileTitlePrefix: 'Profil Roast',
    },
    roastProfileModal: {
      errors: {
        loadFailed: 'Gagal memuat roast profile',
        saveFailed: 'Gagal menyimpan roast profile',
      },
    },
    finishedGoodsTaste: {
      full: 'Penuh',
      viewOtherProfiles: 'Lihat Profil Lain',
      notesPlaceholder: 'Catatan tambahan opsional tentang profil rasa finished product ini',
      saveTasteProfile: 'Simpan Profil Rasa',
      errors: {
        loadExistingFailed: 'Gagal memuat profil rasa yang ada',
        saveFailed: 'Gagal menyimpan profil rasa. Silakan coba lagi.',
        selectFlavorNote: 'Pilih minimal satu catatan rasa.',
      },
    },
    history: {
      title: 'Riwayat Inventory',
      forLot: 'untuk Lot: {{lotNumber}}',
      forRoasted: 'Riwayat Inventory untuk Roasted: {{workOrderNumber}}',
      forFinishedGood: 'Riwayat Inventory untuk FG: {{name}}',
      itemType: 'Tipe Item',
      currentStock: 'Stok Saat Ini',
      cannotAdjustInvalidType: 'Tidak dapat menyesuaikan stok karena tipe item tidak valid atau tidak tersedia.',
      manualStockAdjustment: 'Penyesuaian Stok Manual',
      selectActiveLot: 'Pilih Lot Aktif',
      selectLotOption: '-- Pilih Lot --',
      noActiveLots: 'Tidak ada lot aktif untuk penyesuaian.',
      action: 'Aksi',
      actionAdd: '➕ Tambah Stok',
      actionRemove: '➖ Kurangi Stok',
      quantity: 'Kuantitas',
      unitKg: 'kg',
      placeholderKg: '0.000',
      placeholderGram: '0',
      reasonNotes: 'Alasan / Catatan',
      reasonPlaceholder: 'contoh: cek kerusakan, restok...',
      updateStock: 'Perbarui Stok',
      empty: 'Tidak ada riwayat untuk item ini.',
      lotNumber: 'Nomor Lot',
      quantityUnit: 'Kuantitas ({{unit}})',
      warehouse: 'Gudang',
      user: 'Pengguna',
      errors: {
        unsupportedItemType: 'Tipe item "{{itemType}}" tidak didukung.',
        loadFailed: 'Gagal memuat riwayat inventory.',
        itemIdRequired: 'ID item inventory wajib diisi.',
        selectLotRequired: 'Silakan pilih lot untuk penyesuaian.',
        validQuantityRequired: 'Silakan masukkan kuantitas yang valid.',
        exceedsAvailable: 'Hanya tersedia {{available}} {{unit}} untuk dikurangi.',
        adjustFailed: 'Gagal menyesuaikan stok. Silakan coba lagi.',
      },
    },
    lots: {
      remainingQty: 'Sisa Qty',
      viewHistoryAdjustStock: 'Lihat Riwayat & Sesuaikan Stok',
    },
    table: {
      columns: {
        type: 'Tipe',
        stock: 'Stok',
        averagePrice: 'Harga Rata-rata',
        costOfGoodsSold: 'Harga Pokok Penjualan',
        estimatedSellingPrice: 'Estimasi Harga Jual',
        stockStatus: 'Status Stok',
        unitCost: 'Biaya Unit',
        estimatedPrice: 'Estimasi Harga',
      },
      status: {
        available: 'Tersedia',
        depleted: 'Habis',
        low: 'Stok Menipis',
      },
      actions: {
        clickViewHistory: 'Klik untuk melihat riwayat',
        add: 'Tambah',
        restock: 'Restok',
      },
    },
    stockCost: {
      title: 'Stok & Biaya',
      reorderWarning: 'Peringatan Reorder',
      reorderQuantity: 'Kuantitas Reorder',
    },
    mobile: {
      actions: {
        roast: 'Roast',
        bean: 'Bean',
        taste: 'Taste',
      },
    },
    ingredients: {
      empty: 'Tidak ada bahan ditemukan.',
      itemId: 'ID Item',
    },
    materialHistory: {
      title: 'Riwayat Material (Input Produksi)',
      notFound: 'Riwayat material tidak ditemukan (Work Order ID: {{workOrderId}}).',
      noIngredients: 'Tidak ada bahan tercatat untuk Work Order ini.',
      quantityUsed: 'Kuantitas Dipakai',
      lots: 'Lot',
    },
  },
  artisanSync: {
    bulk: {
      title: 'Unggah Massal',
      uploading: 'Mengunggah {{done}} dari {{total}}...',
      fileName: 'Berkas',
      status: 'Status',
      detail: 'Detail',
      created: 'Dibuat',
      duplicate: 'Sudah pernah diunggah',
      invalid: 'Bukan berkas roasting yang valid',
      failed: 'Gagal',
      fileTooLarge: 'Berkas terlalu besar untuk diunggah',
      chunkFailed: 'Unggahan gagal - coba lagi berkas ini',
      summary: '{{created}} dibuat, {{duplicate}} sudah ada, {{failed}} gagal',
      viewProfile: 'Lihat',
      done: 'Selesai',
    },
    delete: {
      action: 'Hapus',
      deleting: 'Menghapus...',
      selected: 'Hapus terpilih ({{count}})',
      selectedCount: '{{count}} dipilih',
      selectAll: 'Pilih semua profil yang bisa dihapus',
      title: 'Hapus profil roasting?',
      message: '{{count}} profil roasting akan dihapus permanen. Tindakan ini tidak bisa dibatalkan.',
      syncedTooltip: 'Sudah tersinkron ke work order - lepaskan tautannya di sana dulu',
      blocked: 'Tertaut ke work order {{number}}. Lepaskan tautannya di sana dulu.',
      someBlocked: '{{count}} profil dilewati karena tertaut ke work order.',
      failed: 'Gagal menghapus profil roasting',
    },
    file: {
      download: 'Unduh .alog',
      downloading: 'Mengunduh...',
      available: 'Berkas asli tersedia',
      notStored: 'Berkas asli tidak tersimpan',
      notStoredBatch: 'Sebagian profil dibuat tanpa berkas aslinya - penyimpanan berkas tidak tersedia.',
    },
    values: {
      unknown: 'Tidak diketahui',
      unknownOperator: 'Operator Tidak Diketahui',
      untitledRoast: 'Roast Tanpa Judul',
      lightRoast: 'Light',
      workOrder: 'Work Order',
    },
    errors: {
      saveProfile: 'Gagal menyimpan profil ke database',
      syncProfile: 'Gagal sinkron profil ke work order',
      unsupportedFile: 'Tipe file tidak didukung. Mohon unggah file .json atau .alog dari Artisan.',
      invalidFileFields: 'File Artisan tidak valid: field wajib (timex, temp1, temp2) tidak ditemukan.',
      parseFile: 'Gagal memproses file: {{message}}',
      duplicateFile: 'File ini sudah pernah diunggah.',
      unknown: 'Error tidak diketahui',
    },
    page: {
      title: 'Profil Artisan',
      subtitle: 'Kelola dan sinkron data roast yang diekspor dari Artisan Roaster Software',
      uploadNewFile: 'Unggah File Baru',
    },
    filters: {
      allStatus: 'Semua Status',
      syncedToWo: 'Tersinkron ke WO',
      synced: 'Tersinkron',
      unsynced: 'Belum Sinkron',
      searchRoaster: 'Cari Roaster...',
    },
    table: {
      dateTime: 'Tanggal & Waktu',
      roastTitle: 'Judul Roast',
      roaster: 'Roaster',
      machine: 'Mesin',
      status: 'Status',
      action: 'Aksi',
      actionsSr: 'Aksi',
    },
    actions: {
      view: 'Lihat',
    },
    loadingProfiles: 'Memuat profil...',
    emptyProfiles: 'Tidak ada profil Artisan.',
    upload: {
      title: 'Unggah Profil Artisan',
      subtitle: 'Parse dan simpan file Artisan .json atau .alog',
      errorTitle: 'Error Upload',
      dropHere: 'Tarik & lepas file Artisan Anda di sini',
      supportedFormats: 'Mendukung format .json dan .alog',
      bulkHint: 'Pilih lebih dari satu file untuk mengunggah sekaligus',
      browseFiles: 'Pilih File',
      saving: 'Menyimpan...',
      saveToDb: 'Simpan ke Database',
      profilePreview: 'Pratinjau Profil',
      profilePreviewHint: 'Periksa nilainya sebelum disimpan. Setiap perubahan ditandai, dan bisa direset per sel, per kolom, atau seluruh profil.',
    },
    stage: {
      addFiles: 'Tambah file',
      removeFile: 'Hapus',
      discardAll: 'Buang semua',
      saveAll: 'Simpan {{count}} roast',
      reading: 'Membaca {{done}} dari {{total}} file...',
      uploadMore: 'Unggah file lagi',
      resultsTitle: 'Batch tersimpan',
      invalidHelp: 'Hapus file ini dan simpan sisanya, atau ekspor ulang dari Artisan.',
      reason: {
        unsupported: 'Hanya ekspor .json dan .alog dari Artisan yang bisa dibaca.',
        tooLarge: 'File ini melebihi batas unggah 1,5 MB.',
        invalidFields: 'Data roast tidak lengkap - tidak ada timex, temp1, atau temp2 di file ini.',
        parse: 'File ini tidak bisa dibaca sebagai JSON.',
        alreadyStaged: 'Roast ini sudah ada di batch.',
      },
      status: {
        ready: 'Siap disimpan',
        invalid: 'Tidak disimpan',
        saving: 'Menyimpan',
        saved: 'Tersimpan',
        duplicate: 'Sudah pernah diunggah',
        failed: 'Gagal',
      },
    },
    meta: {
      operator: 'Operator',
      machine: 'Mesin',
      beans: 'Beans',
      drumSpeed: 'Kecepatan Drum',
    },
    detail: {
      loadingTitle: 'Memuat Profil',
      loadingBody: 'Memuat detail lengkap profil...',
      syncedToWo: 'Tersinkron ke Work Order: {{workOrderNumber}}',
      unsyncedProfile: 'Profil Belum Sinkron',
      syncToWo: 'Sinkron ke Work Order',
      at: 'pukul',
      roastChart: 'Grafik Roast & Telemetri',
    },
    syncModal: {
      title: 'Sinkron ke Work Order',
      description: 'Pilih work order yang terjadwal dan belum menggunakan roast profile.',
      loadingWo: 'Memuat work order...',
      emptyWo: 'Tidak ada work order yang memenuhi syarat.',
      syncing: 'Menyinkronkan...',
      syncNow: 'Sinkronkan Sekarang',
    },
  },
  aiAssistant: {
    image: {
      attach: 'Lampirkan gambar',
      badType: 'Hanya PNG, JPEG, atau WEBP',
      tooLarge: 'Maks 2MB',
      uploadFailed: 'Gagal mengunggah',
      notSupported: 'Chat gambar tidak didukung pada model saat ini.',
    },
    messages: {
      notConnected: 'AI Assistant belum terhubung.',
      connectApiKey: 'Hubungkan API key',
      notConnectedSuffix: 'di Pengaturan untuk mengakses data Anda.',
      hiThere: 'Saya Qora, AI Assistant Anda.',
      whereToStart: 'Kita mulai dari mana?',
      savedAutomatically: 'Percakapan Anda disimpan otomatis. Tanyakan apa saja untuk memulai.',
      greetings: {
        morning: 'Selamat Pagi! ☀️',
        afternoon: 'Selamat Siang! 🌤️',
        evening: 'Selamat Sore! 🌅',
        night: 'Selamat Malam! 🌙',
      },
      suggestions: {
        s1: 'Apa yang kita kerjakan hari ini?',
        s2: 'Apa yang ingin kamu selesaikan sekarang?',
        s3: 'Apa rencana untuk hari ini?',
        s4: 'Butuh bantuan apa hari ini?',
        s5: 'Apa yang sedang kamu coba cari tahu?',
        s6: 'Apa yang sedang kamu hadapi sekarang?',
        s7: 'Apa yang kamu tangani hari ini?',
        s8: 'Apa yang ingin kamu majukan hari ini?',
        s9: 'Apa yang sedang kita pecahkan bersama?',
        s10: 'Apa yang butuh perhatianmu sekarang?',
        s11: 'Apa yang sedang kamu coba perbaiki?',
        s12: 'Apa fokusmu hari ini?',
        s13: 'Apa hal selanjutnya yang ingin kamu perbaiki atau tingkatkan?',
        s14: 'Apa yang sedang kamu kerjakan sekarang?',
        s15: 'Apa yang ingin kamu bereskan?',
        s16: 'Apa yang ingin kamu perjelas?',
        s17: 'Apa yang sedang kamu coba dorong maju?',
        s18: 'Apa yang layak ditangani sekarang?',
        s19: 'Apa yang ingin kamu buat kemajuannya?',
        s20: 'Apa satu hal yang ingin kamu tangani hari ini?',
      },
      inputPlaceholder: 'Masukkan prompt untuk Qora...',
      send: 'Kirim',
      cannotProcess: 'Maaf, saya tidak bisa memproses permintaan tersebut.',
      backendError: 'Maaf, terjadi error. Pastikan backend sedang berjalan.',
      callingToolsPrefix: 'Memanggil tools',
    },
    loading: {
      progressAria: 'Progres respons asisten',
      findingTool: {
        title: 'Mencari bantuan',
        subtitle: 'Mencari cara terbaik untuk membantu...',
      },
      processingRequest: {
        title: 'Memproses permintaan',
        subtitle: 'Meninjau permintaan Anda...',
        shortSubtitle: 'Mengumpulkan detail...',
      },
      summarizingResponse: {
        title: 'Merangkum jawaban',
        subtitle: 'Menyusun poin-poin penting...',
      },
      preparingAnswer: {
        title: 'Menyiapkan jawaban',
        subtitle: 'Menyiapkan jawaban Anda...',
      },
      longWait: {
        stillWorking: 'Masih diproses untuk Anda...',
        longerThanUsual: 'Terima kasih sudah menunggu, ini sedikit lebih lama dari biasanya.',
        timeout: 'Ini memakan waktu lebih lama dari perkiraan.',
      },
      timeout: {
        message: 'Anda bisa menunggu sebentar lagi atau coba lagi.',
        keepWaiting: 'Tetap menunggu',
        retry: 'Coba lagi',
      },
    },
    chips: {
      createPurchaseOrder: 'Buat purchase order',
      createSalesOrder: 'Buat sales order',
      createInventory: 'Buat inventory',
      createSupplier: 'Buat supplier',
      createCustomer: 'Buat customer',
      createWorkOrderDraft: 'Buat work order draft',
    },
    quickActions: {
      title: 'Aksi Cepat',
      createPurchaseOrderAi: 'Buat Purchase Order AI',
      createSalesOrderAi: 'Buat Sales Order AI',
      createInventory: 'Buat Inventory',
      createSupplier: 'Buat Supplier',
      createCustomer: 'Buat Customer',
      createWorkOrderDraft: 'Buat Work Order Draft',
    },
    prompts: {
      createPurchaseOrder: 'Buat purchase order untuk ',
      createSalesOrder: 'Buat sales order untuk ',
      createInventory:
        'Saya ingin membuat item inventory\n\nNama Item (wajib):\nDeskripsi:\nLokasi:\nKuantitas Awal:\nBiaya Standar:\nReorder Point (kg):\nReorder Quantity (kg):',
      createSupplier:
        'Saya ingin membuat supplier\n\nNama Supplier Bisnis (wajib):\nContact Person:\nNomor Telepon:\nEmail:\nCatatan Internal:',
      createCustomer:
        'Saya ingin membuat customer\n\nNama Customer (wajib):\nEmail (wajib):\nNomor Telepon:\nAlamat Penagihan (wajib):\nAlamat Pengiriman:\nPesan pada Invoice:\nCatatan Internal:',
      createWorkOrderDraft:
        'Saya ingin membuat work order draft\n\nNama Roast:\nMesin:\nRoaster:\nTanggal:\nWaktu:\nCatatan Internal:',
    },
  },
  reports: {
    header: {
      title: 'Laporan Umum',
      exportPdf: 'Export PDF',
      exportExcel: 'Export Excel',
      exporting: 'Mengekspor...',
    },
    modes: {
      roasting: {
        label: 'Laporan Roasting',
        description: 'Proses roasting, batch, dan transformasi berat',
        descriptionHeader: 'Detail batch roasting, transformasi bean, dan insight shrinkage.',
      },
      greenBeans: {
        label: 'Laporan Green Bean',
        description: 'Sisa stok green bean dan insight supplier',
        descriptionHeader: 'Sisa stok green bean beserta detail supplier dan valuasi.',
      },
      roastedBeans: {
        label: 'Laporan Roasted Bulk',
        description: 'Sisa stok roasted bean dan referensi produksi',
        descriptionHeader: 'Sisa inventory roasted bean diurutkan berdasarkan kuantitas tertinggi.',
      },
      finishedGoods: {
        label: 'Laporan Finished Good',
        description: 'Sisa stok finished good dan referensi produksi',
        descriptionHeader: 'Sisa inventory finished good diurutkan berdasarkan kuantitas tertinggi.',
      },
      inventory: {
        label: 'Laporan Inventory',
        description: 'Stok green bean, roasted bulk, dan finished good',
        descriptionHeader: 'Stok inventory green bean, roasted bulk, dan finished good.',
      },
      business: {
        label: 'Laporan Bisnis',
        description: 'Pendapatan, kontribusi pelanggan, dan tren order',
        descriptionHeader: 'Ringkasan pendapatan, rincian pelanggan, dan tren riwayat pembelian.',
      },
      customer: {
        label: 'Laporan Pelanggan',
        description: 'Riwayat pembelian dan ragam produk per pelanggan',
        descriptionHeader: 'Pembelian finished good harian per pelanggan dan produk terlaris.',
      },
    },
    businessSubTabs: {
      sales: 'Penjualan',
      customer: 'Pelanggan',
    },
    customer: {
      listTitle: 'Pelanggan',
      searchPlaceholder: 'Cari pelanggan…',
      emptyList: 'Tidak ada pelanggan yang membeli pada periode ini.',
      selectPrompt: 'Pilih pelanggan untuk melihat riwayat pembeliannya.',
      loading: 'Memuat laporan pelanggan…',
      detailLoading: 'Memuat riwayat pembelian…',
      detailError: 'Gagal memuat riwayat pembelian.',
      orderLoadFailed: 'Gagal membuka sales order.',
      chartTitle: 'Pembelian Finished Good (pcs)',
      emptyChart: 'Tidak ada pembelian finished good pada periode ini.',
      summary: {
        topFinishedGood: 'Paling Banyak Dibeli',
        totalPcs: 'Total Pcs',
        totalOrders: 'Total Order',
        totalRevenue: 'Total Pendapatan',
        none: '—',
      },
      table: {
        orders: 'Order',
        pcs: 'Pcs',
        lastOrder: 'Order Terakhir',
      },
      history: {
        back: 'Kembali',
        date: 'Tanggal',
        invoice: 'No Invoice',
        qty: 'Qty',
        total: 'Total Qty',
        empty: 'Belum ada pembelian untuk finished good ini.',
      },
    },
    export: {
      pdfSuccess: 'Laporan PDF berhasil diekspor.',
      pdfFailed: 'Gagal mengekspor laporan PDF.',
      excelSuccess: 'Laporan Excel berhasil diekspor.',
      excelFailed: 'Gagal mengekspor laporan Excel.',
      sheetName: 'Laporan',
      dataSourceNote: 'Sumber data: endpoint API live dengan fallback data otomatis saat permintaan gagal.',
      sheetCustomers: 'Pelanggan',
      sheetSalesHistory: 'Riwayat Penjualan',
      subtotal: 'Subtotal',
      grandTotal: 'Total Keseluruhan',
      partialFailure: '{{count}} item gagal dimuat dan dilewati.',
      chartSkipped: 'Grafik gagal ditangkap; diekspor tanpa grafik.',
      columns: {
        roastingDate: 'Tanggal Roasting',
        batchNumber: 'Nomor Batch',
        greenBeanCode: 'Kode Green Bean',
        greenBeanName: 'Nama Green Bean',
        inputWeight: 'Berat Input',
        outputWeight: 'Berat Output',
        weightLossPercent: 'Persen Susut Berat',
        itemCode: 'Kode Item',
        itemName: 'Nama Item',
        supplier: 'Supplier',
        remainingQuantity: 'Sisa Kuantitas',
        unit: 'Satuan',
        averageCost: 'Biaya Rata-rata',
        relatedBatch: 'Batch Terkait',
        lastProduction: 'Produksi Terakhir',
        customerName: 'Nama Pelanggan',
        orderCount: 'Jumlah Order',
        totalRevenue: 'Total Pendapatan',
        lastOrderDate: 'Tanggal Order Terakhir',
        totalPcs: 'Total Pcs',
        customerCode: 'Kode Pelanggan',
        finishedGood: 'Finished Good',
        orderNumber: 'No Invoice',
        orderDate: 'Tanggal Order',
        totalAmount: 'Total',
        status: 'Status',
      },
    },
    general: {
      to: 'sampai',
      filter: 'Filter',
      loadingData: 'Memuat data laporan...',
      emptyPeriod: 'Tidak ada data untuk periode ini.',
      showingResults: 'Menampilkan {{from}} sampai {{to}} dari {{total}} hasil',
      table: {
        date: 'Tanggal',
        type: 'Tipe',
        reference: 'Referensi',
        amount: 'Nilai',
        cost: 'Biaya',
        profitLoss: 'Laba/Rugi',
      },
    },
    business: {
      loading: 'Memuat laporan bisnis...',
      kpi: {
        totalRevenue: 'Total Pendapatan',
        totalOrders: 'Total Order',
        activeCustomers: 'Pelanggan Aktif',
        averageOrderValue: 'Nilai Rata-rata Order',
        netProfit: 'Laba Bersih',
      },
      revenueTrend: 'Tren Pendapatan',
      emptyTrend: 'Data tren tidak tersedia.',
      revenueByCustomer: 'Pendapatan per Pelanggan',
      purchaseHistory: 'Riwayat Penjualan',
      emptyCustomerRevenue: 'Data pendapatan pelanggan tidak tersedia.',
      emptyPurchaseHistory: 'Riwayat penjualan tidak tersedia.',
      table: {
        customer: 'Pelanggan',
        orders: 'Order',
        revenue: 'Pendapatan',
        order: 'Order',
        amount: 'Nilai',
      },
    },
    receivables: {
      title: 'Piutang',
      loading: 'Memuat piutang...',
      loadFailed: 'Piutang gagal dimuat.',
      orderLoadFailed: 'Pesanan tidak bisa dibuka.',
      totalLabel: 'Total piutang',
      mostOverdueLabel: 'Paling lama telat',
      noneOverdue: 'Tidak ada yang telat',
      asOf: 'Per {{date}}',
      empty: 'Tidak ada piutang.',
      table: {
        orderNumber: 'No. Pesanan',
        customer: 'Pelanggan',
        orderDate: 'Tanggal pesanan',
        dueDate: 'Jatuh tempo',
        daysOverdue: 'Terlambat',
        amount: 'Jumlah',
      },
      daysValue: '{{days}} hr',
    },
    roastedBeans: {
      loading: 'Memuat laporan roasted bean...',
      empty: 'Data stok roasted bean tidak tersedia untuk rentang terpilih.',
      totalItems: 'Total Item',
      totalRemainingKg: 'Total Sisa (kg)',
      totalStockValue: 'Total Nilai Stok',
      table: {
        itemCode: 'Kode Item',
        itemName: 'Nama Item',
        relatedBatch: 'Batch Terkait',
        remaining: 'Sisa',
        avgCost: 'Rata-rata Biaya',
      },
    },
    finishedGoods: {
      loading: 'Memuat laporan finished good...',
      empty: 'Data stok finished good tidak tersedia untuk rentang terpilih.',
      totalItems: 'Total Item',
      totalRemainingPcs: 'Total Sisa (pcs)',
      totalStockValue: 'Total Nilai Stok',
      table: {
        itemCode: 'Kode Item',
        itemName: 'Nama Item',
        remaining: 'Sisa',
        avgCost: 'Rata-rata Biaya',
        lastProduction: 'Produksi Terakhir',
      },
    },
    tracking: {
      title: 'Pelacakan Inventaris',
      empty: 'Tidak ada riwayat pelacakan',
      error: 'Gagal memuat riwayat pelacakan.',
      filterAll: 'Semua',
      filterIn: 'Masuk',
      filterOut: 'Keluar',
      directionIn: 'Masuk',
      directionOut: 'Keluar',
      table: {
        date: 'Tanggal',
        direction: 'Arah',
        reference: 'Referensi',
        counterparty: 'Pihak Terkait',
        lot: 'Lot',
        quantity: 'Jumlah',
      },
    },
    greenBeans: {
      totalItems: 'Total Item',
      totalRemainingKg: 'Total Sisa (kg)',
      totalStockValue: 'Total Nilai Stok',
      avgCost: 'Biaya Rata-rata',
      loading: 'Memuat laporan green bean...',
      empty: 'Tidak ada data stok green bean untuk rentang terpilih.',
    },
    summary: {
      totalCost: 'Total Biaya',
      grossProfit: 'Laba Kotor',
      margin: 'Margin',
      vsLastMonth: 'vs bulan lalu',
    },
    roasting: {
      totalBatches: 'Total Batch',
      inputWeightKg: 'Berat Input (kg)',
      outputWeightKg: 'Berat Output (kg)',
      weightLossKg: 'Susut Berat (kg)',
      averageLossPercent: 'Rata-rata Susut (%)',
      inputKg: 'Input (kg)',
      outputKg: 'Output (kg)',
      lossPercent: 'Susut (%)',
      loading: 'Memuat laporan roasting...',
      empty: 'Tidak ada data roasting untuk rentang terpilih.',
    },
    details: {
      detailsSuffix: 'Detail',
      titleFallback: 'Detail',
      revenueCalculation: 'Perhitungan Pendapatan',
      salesOrderTotalPrefix: 'Total nilai dari Sales Order',
      formulaRevenue: 'Pendapatan = Jumlah(Quantity × Harga Satuan)',
      profitCalculation: 'Perhitungan Laba',
      salesOrderProfitHelp: 'Sales order diperlakukan sebagai pendapatan, menambah total laba. (Biaya dicatat terpisah melalui Purchase Order).',
      formulaProfitRevenue: 'Laba = Pendapatan',
      costCalculation: 'Perhitungan Biaya',
      purchaseOrderTotalPrefix: 'Total nilai dari Purchase Order',
      formulaCost: 'Biaya = Jumlah(Quantity × Biaya Satuan)',
      purchaseOrderProfitHelp: 'Purchase order diperlakukan sebagai beban, mengurangi total laba.',
      formulaProfitCost: 'Laba = -Biaya',
      valueAdjustment: 'Penyesuaian Nilai',
      valueAdjustmentHelp: 'Perubahan nilai inventaris akibat penyesuaian manual.',
      reason: 'Alasan',
      formulaValueChange: 'Perubahan Nilai = Quantity × Biaya Standar',
      noDetails: 'Detail tidak tersedia.',
      notes: 'Catatan',
      unitCost: 'Biaya Satuan',
      orderItems: 'Item Order',
      loadingItems: 'Memuat item...',
      qty: 'Qty',
      unitPrice: 'Harga Satuan',
      total: 'Total',
      noItems: 'Tidak ada item untuk order ini.',
      howCalculated: 'Bagaimana ini dihitung?',
      actions: {
        editMovement: 'Ubah Pergerakan',
        saveChanges: 'Simpan Perubahan',
        cancelEdit: 'Batal Ubah',
      },
    },
    shift: {
      title: 'Manajemen Shift',
      subtitle: 'Buka dan tutup shift untuk melacak pergerakan harian.',
      currentStatus: 'Status Saat Ini',
      loadingCurrent: 'Memuat shift saat ini...',
      errorCurrent: 'Gagal memuat shift saat ini.',
      openTitle: 'Shift Sedang Dibuka',
      startedAt: 'Dimulai pada {{value}}',
      closeShift: 'Tutup Shift',
      openedByTitle: 'Dibuka oleh {{name}}',
      openedBy: 'Dibuka Oleh',
      closedBy: 'Ditutup Oleh',
      currentSales: 'Penjualan Saat Ini',
      cost: 'Biaya',
      profit: 'Laba',
      transactions: 'Transaksi',
      ordersCount: '{{count}} Order',
      noActiveTitle: 'Tidak Ada Shift Aktif',
      noActiveDescription:
        'Mulai shift baru untuk mulai melacak penjualan dan pergerakan inventaris pada periode saat ini.',
      startShift: 'Mulai Shift',
      historyTitle: 'Riwayat Shift',
      loadingHistory: 'Memuat riwayat...',
      errorHistory: 'Gagal memuat riwayat.',
      period: 'Periode',
      totalSales: 'Total Penjualan',
      status: 'Status',
      view: 'Lihat',
      closed: 'Ditutup',
      viewDetails: 'Lihat Detail',
      startNewTitle: 'Mulai Shift Baru',
      startNewDescription:
        'Apakah Anda yakin ingin memulai shift baru? Ini akan mulai melacak semua transaksi baru dan pergerakan inventaris.',
      summaryTitle: 'Ringkasan Shift',
      totalPurchases: 'Total Pembelian',
      netCashFlow: 'Arus Kas Bersih',
      totalTransactions: 'Total Transaksi',
      itemsIn: 'Item Masuk',
      itemsOut: 'Item Keluar',
      financialTransactions: 'Transaksi Keuangan',
      id: 'ID',
      type: 'Tipe',
      amount: 'Nilai',
      time: 'Waktu',
      noTransactions: 'Tidak ada transaksi',
      inventoryMovements: 'Pergerakan Inventaris',
      item: 'Item',
      quantity: 'Kuantitas',
      reason: 'Alasan',
      noMovements: 'Tidak ada pergerakan',
      reviewBeforeClose: 'Mohon tinjau ringkasan sebelum konfirmasi penutupan.',
      onlyOpenerCanClose: 'Hanya {{name}} yang bisa menutup shift ini',
      confirmClose: 'Konfirmasi Tutup',
      toast: {
        closedSuccess: 'Shift berhasil ditutup',
        closeFailed: 'Gagal menutup shift',
      },
    },
  },
  settingsMcp: {
    title: 'Integrasi MCP',
    subtitle: 'Kelola API key untuk agen Model Context Protocol (MCP) agar dapat mengakses backend Anda.',
    confirmRevoke: 'Apakah Anda yakin ingin mencabut API key ini? Tindakan ini tidak dapat dibatalkan.',
    errors: {
      loadKeys: 'Gagal memuat API key',
      createKey: 'Gagal membuat API key',
      revokeKey: 'Gagal mencabut API key',
    },
    connection: {
      title: 'Koneksi AI Assistant',
      connected: 'Terhubung — AI assistant menggunakan API key Anda untuk mengakses data.',
      disconnected: 'Terputus — Buat key lalu hubungkan untuk menggunakan AI assistant dengan akun Anda.',
      manualHelp: 'Tempel API key yang sudah ada untuk menghubungkan AI assistant:',
      manualPlaceholder: 'Tempel API key Anda di sini...',
    },
    createForm: {
      title: 'Buat API Key Baru',
      namePlaceholder: 'Nama Key (contoh: Claude Desktop)',
    },
    newKey: {
      title: 'MCP API Key Berhasil Dibuat',
      description: 'Silakan salin dan simpan key ini sekarang. Key hanya ditampilkan sekali.',
      copyTooltip: 'Klik tombol salin untuk menyalin key',
    },
    table: {
      name: 'Nama',
      keyPrefix: 'Prefiks Key',
      created: 'Dibuat',
      lastUsed: 'Terakhir Digunakan',
      status: 'Status',
      actions: 'Aksi',
    },
    docs: {
      title: 'Detail Koneksi',
      description: 'Untuk menghubungkan klien MCP (seperti Claude Desktop), gunakan konfigurasi berikut:',
      replacePrefix: 'Ganti',
      replaceSuffix: 'dengan key yang dihasilkan di atas.',
      yourApiKeyPlaceholder: 'YOUR_API_KEY_HERE',
    },
    values: {
      none: '-',
    },
    status: {
      active: 'Aktif',
      revoked: 'Dicabut',
    },
    actions: {
      generateNewKey: 'Buat Key Baru',
      disconnect: 'Putuskan',
      connectKey: 'Hubungkan Key',
      connect: 'Hubungkan',
      create: 'Buat',
      copied: 'Tersalin',
      copyKey: 'Salin Key',
      connectToAssistant: 'Hubungkan ke AI Assistant',
      done: 'Selesai',
      copyPrefix: 'Salin Prefiks',
      revoke: 'Cabut',
      copyToClipboard: 'Salin ke clipboard',
    },
  },
  settingsMember: {
    title: 'Anggota Tim',
    subtitle: 'Kelola siapa saja yang memiliki akses ke workspace ini.',
    loading: 'Memuat anggota...',
    fields: {
      email: 'Email',
      password: 'Password',
      emailAddress: 'Alamat Email',
      emailPlaceholder: 'rekan@perusahaan.com',
      fullName: 'Nama Lengkap',
      fullNamePlaceholder: 'Budi Santoso',
      role: 'Role',
    },
    values: {
      unknown: 'Tidak Diketahui',
      you: '(Anda)',
    },
    roles: {
      admin: 'Admin',
      manager: 'Manager',
      operator: 'Operator',
      viewer: 'Viewer',
    },
    status: {
      active: 'Aktif',
      inactive: 'Tidak Aktif',
      pending: 'Menunggu',
    },
    actions: {
      inviteMember: 'Undang Anggota',
      sendInvite: 'Kirim Undangan',
      copyPassword: 'Salin Password',
      done: 'Selesai',
      resetPassword: 'Reset Password',
      removeMember: 'Hapus Anggota',
      reactivateMember: 'Aktifkan Kembali',
      editRole: 'Ubah Role',
      updateRole: 'Simpan Role',
      understand: 'Saya Mengerti',
      continue: 'Lanjutkan',
      back: 'Kembali',
    },
    errors: {
      sendInvite: 'Gagal mengirim undangan',
      resetPassword: 'Gagal reset password',
      removeMember: 'Gagal menghapus anggota',
      reactivateMember: 'Gagal mengaktifkan kembali anggota',
      updateRole: 'Gagal memperbarui role',
      duplicateActive: 'Anggota dengan email ini sudah ada di perusahaan Anda.',
      duplicateInactive: 'Anggota nonaktif dengan email ini sudah ada. Anda dapat mengaktifkannya kembali.',
      duplicateTitle: 'Email sudah digunakan',
      emailInUse: 'Email ini sudah terdaftar.',
    },
    toast: {
      memberAddedTitle: 'Anggota Berhasil Ditambahkan',
      passwordResetTitle: 'Reset Password Berhasil',
      roleUpdatedTitle: 'Role Berhasil Diperbarui',
      roleUpdatedMessage: 'Role untuk {{email}} berhasil diperbarui',
    },
    passwordModal: {
      description: 'Silakan salin dan bagikan kredensial ini secara aman. Password tidak akan ditampilkan lagi.',
    },
    modals: {
      resetTitle: 'Reset Password',
      resetDescriptionPrefix: 'Apakah Anda yakin ingin reset password untuk',
      resetDescriptionSuffix: '? Tindakan ini akan membuat password acak baru dan menonaktifkan password lama.',
      removeTitle: 'Hapus Anggota',
      removeDescriptionPrefix: 'Apakah Anda yakin ingin menghapus',
      removeDescriptionSuffix: '? Mereka tidak akan bisa lagi mengakses sistem.',
      removeWarning: 'Ini akan menonaktifkan akun. Mereka tidak akan bisa login lagi, tetapi Anda dapat mengaktifkannya kembali nanti.',
      reactivateTitle: 'Aktifkan Kembali Anggota',
      reactivateDescription: 'Apakah Anda ingin mengaktifkan kembali',
      editRoleTitle: 'Ubah Role Anggota',
      editRoleDescription: 'Perbarui role untuk',
      editRoleNote: 'Role baru akan berlaku saat anggota login kembali.',
      editRoleCurrentLabel: 'Role Saat Ini',
      editRoleNewLabel: 'Role Baru',
      adminInviteWarningTitle: 'Role Admin Tidak Dapat Diubah',
      adminInviteWarningBody: 'Anda mengundang anggota ini sebagai Admin. Role Admin bersifat permanen dan tidak dapat diubah di kemudian hari. Apakah Anda yakin ingin melanjutkan?',
    },
    filters: {
      search: 'Cari',
      searchPlaceholder: 'Cari berdasarkan nama atau email...',
      allRoles: 'Semua Role',
      allStatuses: 'Semua Status',
      noResults: 'Tidak ada anggota yang cocok dengan kriteria filter Anda.',
    },
  },
  settingsArtisan: {
    title: 'Integrasi Artisan Perusahaan',
    subtitle: 'Buat API key untuk integrasi exporter Artisan eksternal milik perusahaan Anda.',
    confirmRevoke: 'Apakah Anda yakin ingin mencabut API key ini?',
    errors: {
      loadKeys: 'Gagal memuat Artisan API key',
      createKey: 'Gagal membuat Artisan API key',
      revokeKey: 'Gagal mencabut Artisan API key',
    },
    createForm: {
      title: 'Buat Artisan API Key Baru',
      namePlaceholder: 'Nama Key (contoh: Laptop Roaster)',
    },
    newKey: {
      title: 'Artisan API Key Berhasil Dibuat',
      description: 'Salin dan simpan key ini sekarang. Key hanya ditampilkan sekali.',
      copyTooltip: 'Klik tombol salin untuk menyalin key',
    },
    table: {
      name: 'Nama',
      keyPrefix: 'Prefiks Key',
      created: 'Dibuat',
      lastUsed: 'Terakhir Digunakan',
      status: 'Status',
      actions: 'Aksi',
    },
    values: {
      none: '-',
    },
    status: {
      active: 'Aktif',
      revoked: 'Dicabut',
    },
    actions: {
      generateNewKey: 'Buat Key Baru',
      create: 'Buat',
      copied: 'Tersalin',
      copyKey: 'Salin Key',
      done: 'Selesai',
      revoke: 'Cabut',
    },
  },
  forms: {
    common: {
      autoGenerated: 'Otomatis',
      generatedAutomatically: 'Dibuat otomatis',
      email: 'Email',
      phone: 'Telepon',
      status: 'Status',
      statusActive: 'Aktif',
      statusInactive: 'Tidak Aktif',
      internalNotes: 'Catatan Internal',
      loadingHistory: 'Memuat riwayat...',
      date: 'Tanggal',
      amount: 'Nilai',
      poNumber: 'Nomor PO',
      orderNumber: 'Nomor Order',
      all: 'Semua',
    },
    actions: {
      sendEmail: 'Kirim Email',
      openWhatsapp: 'Buka WhatsApp',
      call: 'Telepon',
      close: 'Tutup',
      saving: 'Menyimpan...',
      save: 'Simpan',
    },
    supplier: {
      name: 'Nama',
      code3: 'Kode (3 Karakter)',
      contactName: 'Nama Kontak',
      purchaseOrderHistory: 'Riwayat Purchase Order',
      emptyPurchaseOrders: 'Tidak ada purchase order untuk supplier ini.',
      poStatus: {
        draft: 'Draft',
        ordered: 'Dipesan',
        cancelled: 'Dibatalkan',
      },
    },
    customer: {
      name: 'Nama',
      code3: 'Kode (3 Karakter)',
      billingAddress: 'Alamat Penagihan',
      shippingAddress: 'Alamat Pengiriman',
      sameAsBilling: 'Sama dengan alamat penagihan',
      messageOnInvoice: 'Pesan pada Invoice',
      salesOrderHistory: 'Riwayat Sales Order',
      emptySalesOrders: 'Tidak ada sales order untuk customer ini.',
      emailCounter: '{{count}}/{{max}} karakter',
      hints: {
        enterEmailToSend: 'Masukkan email untuk mengirim pesan',
        enterPhoneForWhatsapp: 'Masukkan nomor untuk WhatsApp',
        enterPhoneForCall: 'Masukkan nomor untuk telepon',
      },
      soStatus: {
        Draft: 'Draft',
        Sent: 'Terkirim',
        Delivered: 'Selesai Dikirim',
        Cancelled: 'Dibatalkan',
      },
      itemName: 'Nama Item',
    },
  },
  recipes: {
    form: {
      preheat: 'Preheat',
      recipeName: 'Nama Resep',
      internalNotesPlaceholder: 'Catatan internal hanya terlihat oleh staf...',
      targetBeanWeight: 'Target Berat Bean',
      totalTimeMinutes: 'Total Waktu (menit)',
      shelfLifeDays: 'Masa Simpan (hari)',
      roastStages: 'Tahap Roast',
      addStage: 'Tambah Tahap',
      stageName: 'Nama Tahap',
      stageNamePlaceholder: 'contoh: Drying',
      tempC: 'Suhu (°C)',
      timeMin: 'Waktu (menit)',
      update: 'Perbarui',
      create: 'Buat',
    },
    roastProfileChart: {
      addStagesToView: 'Tambahkan tahap roast untuk melihat profil',
      visualization: 'Visualisasi Profil Roast',
    },
    tasting: {
      title: 'Ubah Catatan Cupping',
      flavorTags: 'Tag Rasa',
      generalNotes: 'Catatan Umum',
      placeholderTag: 'Ketik untuk menambah tag rasa...',
      placeholderNotes: 'Deskripsikan kesan keseluruhan...',
      saveNotes: 'Simpan Catatan',
      removeTag: 'Hapus {{tag}}',
      attributes: {
        Acidity: 'Acidity',
        Aftertaste: 'Aftertaste',
        'Clean Cup': 'Clean Cup',
        Head: 'Head',
        Fragrance: 'Fragrance',
        Sweetness: 'Sweetness',
        Balance: 'Balance',
        Aroma: 'Aroma',
        Body: 'Body',
      },
    },
    tastingSection: {
      title: 'Profil Cupping',
      empty: 'Belum ada catatan cupping.',
      addNotes: 'Tambah Catatan Cupping',
      editNotes: 'Ubah Catatan',
      noTags: 'Belum ada tag',
      noNotes: 'Belum ada catatan',
    },
    page: {
      loading: 'Memuat resep...',
      searchPlaceholder: 'Cari profil...',
      empty: 'Tidak ada profil yang cocok dengan filter.',
      fetchingDetails: 'Mengambil detail profil...',
      columns: {
        itemCode: 'Item & Kode',
        targetWeightInOut: 'Target Berat (In/Out)',
      },
      actions: {
        newProfile: 'Profil Baru',
        updateProfile: 'Perbarui Profil',
        createNewProfile: 'Buat Profil Baru',
        deleting: 'Menghapus...',
      },
      errors: {
        loadFailed: 'Gagal memuat resep. Silakan coba lagi.',
        deleteFailed: 'Gagal menghapus profil. Silakan coba lagi.',
      },
      moreCount: '+{{count}} lainnya',
      shelfLifeDaysCount: '{{days}} hari',
      viewDetailsArrow: 'Lihat Detail →',
      confirmDeletePrefix: 'Apakah Anda yakin ingin menghapus profil',
    },
  },
  greenBeanProfile: {
    title: 'Profil Green Bean',
    loading: 'Memuat profil green bean...',
    search: 'Cari',
    searchPlaceholder: 'Cari berdasarkan Kode Profil, Nama, Asal, Negara...',
    itemName: 'Nama Item',
    allItems: 'Semua Item',
    selectedCount: '{{count}} dipilih',
    emptyItems: 'Tidak ada item',
    emptyProfiles: 'Tidak ada profil.',
    grade: 'Grade {{grade}}',
    values: {
      noCode: 'Tanpa Kode',
      noName: 'Tanpa Nama',
      defaultProfile: 'Profil Default',
    },
    actions: {
      new: 'Green Bean Baru',
      edit: 'Ubah',
      delete: 'Hapus',
      update: 'Perbarui Profil',
      create: 'Buat Profil',
    },
    errors: {
      loadFailed: 'Gagal memuat profil',
      deleteFailed: 'Gagal menghapus profil',
      saveFailed: 'Gagal menyimpan profil. Silakan coba lagi.',
      applyPresetFailed: 'Gagal menerapkan preset profil item. Silakan coba lagi.',
      skipPresetFailed: 'Gagal menutup popup preset. Silakan coba lagi.',
    },
    useItemProfileTitle: 'Gunakan profil green bean item?',
    useItemProfileMessage: 'Item pada lot ini sudah memiliki profil green bean. Apakah Anda ingin menggunakannya?',
    deleteModal: {
      title: 'Hapus Profil',
      message: 'Apakah Anda yakin ingin menghapus profil green bean ini? Tindakan ini tidak dapat dibatalkan.',
    },
  },
  greenBean: {
    quality: {
      title: 'Kualitas & Grading',
      grade: 'Grade',
      gradeLabel: 'Grade {{grade}}',
      color: 'Warna',
      moistureContent: 'Kadar Air (%)',
      qcCupping: 'QC Cupping',
      selectType: 'Pilih Tipe',
      aromaMulti: 'Aroma (Pilih lebih dari satu)',
      tasteProfile: 'Profil Rasa',
      tasteProfilePlaceholder: 'Deskripsikan profil rasa...',
      errors: {
        gradeRequired: 'Grade wajib diisi',
        colorRequired: 'Warna wajib diisi',
      },
    },
    physical: {
      title: 'Penilaian Fisik',
      selectGrade: 'Pilih Grade',
      selectColor: 'Pilih warna',
      density: 'Densitas (g/L)',
      densityPlaceholder: 'contoh: 720',
      errors: {
        moistureRequired: 'Kadar air wajib diisi',
      },
    },
    processing: {
      title: 'Processing',
      processorName: 'Nama Processor',
      selectProcessType: 'Pilih Tipe Proses',
      otherDetails: 'Detail Processing Lainnya',
      notes: 'Catatan Processing',
    },
    itemInfo: {
      title: 'Informasi Item',
      workOrder: 'Work Order',
    },
    origin: {
      title: 'Asal',
      notes: 'Catatan Asal',
    },
    extrinsic: {
      title: 'Ekstrinsik',
      country: 'Negara',
      province: 'Provinsi',
      region: 'Wilayah',
      farm: 'Nama Kebun atau Koperasi',
      processor: 'Nama Pengolah',
      wetMill: 'Wet Mill / Station',
      dryMill: 'Dry Mill',
      other: 'Lainnya',
      harvestDateYear: 'Tanggal / Tahun Panen',
      placeholderCountry: 'Pilih atau ketik negara',
      placeholderProvince: 'Pilih atau tambah provinsi',
      placeholderRegion: 'Pilih atau tambah wilayah',
      placeholderFarm: 'Pilih atau tambah kebun',
      placeholderProcessor: 'Pilih atau tambah pengolah',
      errors: {
        specifyProcessType: 'Silakan isi tipe proses',
      },
    },
  },
  settingsBilling: {
    title: 'Tagihan & Penggunaan',
    proPlanActive: 'Paket Pro Aktif',
    currentPlan: 'Paket Saat Ini',
    planName: 'Paket Pro',
    planBilling: '$29.00 / bulan • Ditagih bulanan',
    nextBillingDate: 'Tanggal tagihan berikutnya',
    nextBillingDateValue: '1 November 2023',
    usageLimits: 'Batas Penggunaan',
    paymentMethod: 'Metode Pembayaran',
    visaEnding: 'Visa berakhir 4242',
    expires: 'Berakhir 12/2024',
    cardNumber: 'Nomor Kartu',
    expiryDate: 'Tanggal Kedaluwarsa',
    cvc: 'CVC',
    billingHistory: 'Riwayat Tagihan',
    invoice: 'Invoice',
    pdf: 'PDF',
    metrics: {
      Storage: 'Penyimpanan',
      'Team Members': 'Anggota Tim',
      'API Calls': 'Panggilan API',
    },
    status: {
      paid: 'Lunas',
      pending: 'Menunggu',
      failed: 'Gagal',
    },
    actions: {
      update: 'Perbarui',
      saveCard: 'Simpan Kartu',
    },
    placeholders: {
      cardNumber: '0000 0000 0000 0000',
      expiry: 'MM/YY',
      cvc: '123',
    },
    toast: {
      cardUpdated: 'Detail kartu berhasil diperbarui!',
    },
  },
  settingsAdminMembers: {
    title: 'Anggota Admin',
    subtitle: 'Akun admin lintas platform untuk semua perusahaan.',
    loading: 'Memuat anggota admin...',
    retry: 'Coba Lagi',
    empty: 'Tidak ada anggota admin.',
    noResults: 'Tidak ada anggota admin yang cocok dengan pencarian.',
    searchPlaceholder: 'Cari berdasarkan email, nama lengkap, atau perusahaan...',
    companyName: 'Nama Perusahaan',
    unknownCompany: 'Perusahaan Tidak Diketahui',
    errors: {
      loadFailed: 'Gagal memuat anggota admin.',
      unableLoad: 'Tidak dapat memuat anggota admin',
    },
  },
  settingsCompany: {
    title: 'Profil Perusahaan',
    subtitle: 'Perbarui informasi perusahaan Anda. Ini akan digunakan pada invoice dan dokumen lainnya.',
    logo: 'Logo Perusahaan',
    removeLogo: 'Hapus logo',
    companyName: 'Nama Perusahaan',
    address: 'Alamat',
    website: 'Website',
    websitePlaceholder: 'https://contoh.com',
    currencySymbol: 'Simbol Mata Uang',
    currencyPlaceholder: 'contoh: Rp, $, €',
    savedSuccess: 'Berhasil disimpan!',
    errors: {
      updateFailed: 'Gagal memperbarui profil perusahaan',
      logoMaxSize: 'Logo harus kurang dari 2MB',
      logoType: 'Logo harus berupa gambar PNG, JPG, atau WEBP',
      uploadFailed: 'Gagal mengunggah logo',
      deleteFailed: 'Gagal menghapus logo',
    },
  },
  roast: {
    profile: {
      stages: {
        start: 'Mulai',
        turningPoint: 'Turning Point',
        yellow: 'Yellow',
        firstCrack: 'First Crack',
        end: 'Selesai',
        preheat: 'Preheat',
      },
      legend: {
        beanTemp: 'Suhu Bean',
        ror: 'ROR',
        fire: 'Api',
        airflow: 'Airflow',
        airTemp: 'Suhu Udara',
        drumTemp: 'Suhu Drum',
        environmentTemp: 'Suhu Lingkungan',
        drumRpm: 'RPM Drum',
      },
      phases: {
        drying: 'Drying',
        maillard: 'Maillard',
        development: 'Development',
      },
      markers: {
        charge: 'CHARGE',
        tp: 'TP',
        fcs: 'FCs',
        drop: 'DROP',
      },
    },
    agtron: {
      targetAgtronMeter: 'Target Agtron Meter',
      agtronMeter: 'Agtron Meter',
      wholeBean: 'Whole Bean',
      groundBean: 'Ground Bean',
      difference: 'Perbedaan',
      scaa: 'SCAA',
      common: 'Umum',
      zeroPlaceholder: '0',
    },
    weightAnalysis: {
      targetWeightAnalysis: 'Analisis Berat Target',
      weightAnalysis: 'Analisis Berat',
      differsCalculated: 'Peringatan: Berbeda dari hasil kalkulasi ({{weight}} {{unit}})',
      largeShrinkage: 'Peringatan: Shrinkage besar',
      outputGreater: 'Output roast tidak boleh lebih besar dari input roast.',
    },
    timePhases: {
      targetTimePhases: 'Target Fase Waktu',
      timePhases: 'Fase Waktu',
      dryingPhase: 'Fase Drying',
      startToYellow: 'Mulai hingga Kuning',
      maillardPhase: 'Fase Mailard',
      yellowToFirstCrack: 'Kuning hingga 1st Crack',
      developmentPhase: 'Fase Development',
      firstCrackToEnd: '1st Crack hingga Selesai',
    },
    lotSection: {
      lotsUsed: 'Lot Terpakai',
      addLot: 'Tambah Lot',
      totalUsedWeight: 'Total Berat Terpakai ({{unit}})',
      totalUsedWeightMismatch: 'Total berat terpakai dari lot berbeda dengan Roast Input',
      selectLot: 'Pilih Lot',
      density: 'Densitas (gr/L)',
      processType: 'Tipe Proses',
      before: 'Sebelum',
      after: 'Sesudah',
      useWeight: 'Berat Pakai',
      exceedsRemainingStock: 'Melebihi stok tersisa',
      afterUse: 'Setelah Dipakai',
    },
    stageData: {
      targetStageData: 'Data Tahap Target',
      stageData: 'Data Tahap',
      temperature: 'Suhu',
      celsius: '°C',
      mask: {
        minute: 'M',
        second: 'S',
      },
    },
    formNotes: {
      enterNotes: 'Masukkan catatan di sini...',
    },
    page: {
      title: 'Roasting',
      newRoast: 'Roast Baru',
      createNewRoast: 'Buat Roast Baru',
    },
    form: {
      unnamedProfile: 'Profil Tanpa Nama',
      unknownBean: 'Bean Tidak Diketahui',
      errors: {
        saveRecipeFailed: 'Gagal menyimpan resep',
        saveRoastFailed: 'Gagal menyimpan roast',
      },
      success: {
        savedSuccessfully: 'Roast berhasil disimpan!',
      },
    },
    profileTable: {
      additionalColumn: 'Kolom Tambahan',
      noneSelected: 'Belum Dipilih',
      selected: 'dipilih',
      selectedCount: '{{count}} dipilih',
      addRow: 'Tambah Baris',
      manualTimeEntry: 'Input Waktu Manual',
      min: 'Menit',
      sec: 'Detik',
      addToProfile: 'Tambah ke Profil',
      temp: 'Suhu',
      ror: 'ROR',
      fire: 'Fire',
      airflow: 'Airflow',
      airTemp: 'Suhu Udara',
      drumTemp: 'Suhu Drum',
      environmentTemp: 'Suhu Lingkungan',
      drumRpm: 'RPM Drum',
      removeRow: 'Hapus baris ini',
      edits: {
        summaryOne: '1 diubah manual',
        summaryMany: '{{count}} diubah manual',
        resetAll: 'Reset semua',
        reset: 'Reset',
        resetField: 'Reset {{field}}',
        revertTo: 'Kembalikan ke nilai impor {{value}}',
        revertToEmpty: 'Kosongkan lagi seperti hasil impor',
        andMore: '+{{count}} lagi',
      },
      errors: {
        secondsRange: 'Detik harus 0-59.',
        invalidTime: 'Waktu tidak valid.',
        afterLastRow: 'Waktu harus setelah baris terakhir ({{lastTime}})',
        within30s: 'Waktu harus dalam 30 detik setelah baris terakhir (sebelum {{before}})',
      },
    },
    profileForm: {
      roastDate: 'Tanggal Roast',
      required: 'Wajib diisi',
      errors: {
        invalidJson: 'File JSON tidak valid. Pastikan file berisi struktur roast profile yang benar.',
        entityTypeMissing: 'Kesalahan Internal: Entity Type tidak ditemukan',
        saveFailed: 'Gagal menyimpan roast profile',
      },
    },
    formHeader: {
      profileName: 'Nama Profil',
      enterProfileName: 'Masukkan nama profil',
      machine: 'Mesin',
      selectMachineType: 'Pilih tipe mesin',
      beanItem: 'Item Bean',
      selectBean: 'Pilih bean',
      roastNumber: 'Nomor Roast',
      recipeName: 'Nama Resep',
      enterRecipeName: 'Masukkan nama resep',
      roaster: 'Roaster',
      enterRoasterName: 'Masukkan nama roaster',
    },
  },
  cuppingSessions: {
    title: 'Sesi Cupping',
    searchPlaceholder: 'Cari kode cupping atau cupper...',
    loading: 'Memuat sesi cupping...',
    empty: 'Tidak ada sesi cupping yang cocok dengan filter.',
    columns: {
      code: 'Kode',
      cupper: 'Cupper',
      items: 'Item',
      draftDate: 'Tanggal Draft',
      completeDate: 'Tanggal Selesai',
    },
    status: {
      Draft: 'Draft',
      Complete: 'Selesai',
      all: 'Semua',
    },
    actions: {
      new: 'Sesi Cupping Baru',
      open: 'Buka',
    },
    create: {
      title: 'Sesi Cupping Baru',
      loadingData: 'Memuat data...',
      selectCupper: 'Pilih cupper',
      cuppingMode: 'Mode Cupping',
      modeLockedNote: '*Ini tidak dapat diubah setelah dibuat.',
      roastedBulkSelection: 'Pemilihan Roasted Bulk',
      searchBulkPlaceholder: 'Cari work order, nama item, tipe roast',
      allBulksAvailable: 'Semua roasted bulk tersedia.',
      practiceRoastLimit: 'Cupping latihan membandingkan dua roasting. Hapus salah satu di bawah untuk mengganti pilihan.',
      enableBlindMode: 'Aktifkan blind mode',
      blindCode: 'Kode Blind',
      noCode: 'Tanpa Kode',
      saveDraft: 'Simpan Draft',
      confirmTitle: 'Konfirmasi Mode Cupping',
      confirmLock: 'Mode cupping tidak dapat diubah nanti',
      selectedModePrefix: 'Anda memilih mode',
      selectedModeSuffix: '. Ini akan dikunci untuk seluruh sesi.',
      preview: 'Pratinjau',
      previewMode: 'Pratinjau {{mode}}',
      formPreviewDisabled: 'Pratinjau Form (Dinonaktifkan)',
      readOnly: 'HANYA-BACA',
      closePreview: 'Tutup Pratinjau',
      errors: {
        selectCupper: 'Silakan pilih cupper.',
        selectMinItems: 'Silakan pilih minimal 2 roasted bulk item.',
        itemInDraft: {
          title: 'Item sudah dipakai di draft lain',
          intro: 'Roasted bulk berikut sudah terdaftar di draft cupping session lain dan belum bisa digunakan sampai draft tersebut diselesaikan atau item-nya dihapus:',
          inSession: 'di draft {{code}}',
          hint: 'Buka draft tersebut, selesaikan atau lepas itemnya, lalu coba lagi.',
          dismiss: 'Mengerti',
        },
        unknown: 'Cupping session gagal disimpan. Silakan coba lagi.',
      },
    },
    form: {
      errors: {
        completeAllTabs: 'Silakan selesaikan semua tab item sebelum menyelesaikan sesi.',
      },
      loadingSession: 'Memuat sesi cupping...',
      notFound: 'Sesi cupping tidak ditemukan.',
      backToSessions: 'Kembali ke sesi',
      blindModeEnabled: 'Blind Mode Aktif',
      standardMode: 'Mode Standar',
      clickToViewDetails: 'Klik untuk melihat detail item',
      holdToViewDetails: 'Tahan untuk melihat detail item',
      reveal: 'Tampilkan',
      completedCount: '{{completed}}/{{total}} selesai',
      completeSession: 'Selesaikan Sesi',
      confirmCompleteTitle: 'Konfirmasi Selesaikan Sesi',
      profilesLockedTitle: 'Profil tidak dapat diedit setelah selesai',
      profilesLockedMessage: 'Setelah sesi ini diselesaikan, semua profil cupping akan terkunci permanen dan tidak dapat diubah. Jika perlu perubahan, Anda harus membuat profil cupping baru.',
      roastViewer: {
        title: 'Profil Roast Work Order',
        workOrderNotFound: 'Work order tidak ditemukan.',
        noRoastProfile: 'Work order ini tidak memiliki profil roast.',
        dataLog: 'Log Data Roast',
        profileCurve: 'Kurva Profil Roast',
        notes: 'Catatan',
        header: {
          workOrder: 'Work Order',
          machine: 'Mesin',
          roaster: 'Roaster',
          completed: 'Selesai',
          roastSuffix: 'Roast',
        },
        table: {
          time: 'Waktu',
          temp: 'Suhu',
          ror: 'ROR',
        },
        analysis: {
          in: 'Masuk',
          out: 'Keluar',
          loss: 'Susut',
          whole: 'Biji Utuh',
          ground: 'Biji Giling',
          diffType: 'Selisih / Jenis',
        },
        beans: {
          title: 'Green Bean & Lot',
          greenBean: 'Green Bean',
          lots: 'Lot',
          noLots: 'Tidak ada lot tercatat.',
        },
      },
    },
  },
  cuppingProfileForm: {
    confirmSaveTitle: 'Konfirmasi Simpan Profil',
    lockedTitle: 'Profil tidak dapat diedit setelah disimpan',
    lockedMessage: 'Setelah disimpan, profil cupping ini akan terkunci permanen dan tidak dapat diubah. Jika perlu perubahan, Anda harus membuat profil cupping baru.',
    errors: {
      selectDefectTypeWhenDefectiveCups: 'Silakan pilih minimal satu jenis defect saat "Defective Cups" > 0.',
    },
  },
  lotSelector: {
    loading: 'Memuat lot...',
    empty: 'Tidak ada lot tersedia',
    selectLots: 'Pilih Lot',
    needMore: 'Perlu {{amount}} {{unit}} lagi',
    requirementMet: 'Kebutuhan Terpenuhi',
    expires: 'Kedaluwarsa',
    qtyPlaceholder: 'Qty',
    allocatedExceedsRequired: 'Peringatan: Kuantitas alokasi ({{allocated}}) melebihi kebutuhan ({{required}})',
    errors: {
      loadFailed: 'Gagal memuat lot',
    },
  },
  profileManager: {
    greenBean: {
      edit: 'Ubah Profil Green Bean',
      add: 'Tambah Profil Green Bean',
    },
    roast: {
      edit: 'Ubah Profil Roast',
      add: 'Tambah Profil Roast',
    },
    cupping: {
      add: 'Tambah Profil Cupping',
      view: 'Lihat Profil Cupping',
    },
    actions: {
      add: 'Tambah',
    },
    selectCuppingDate: 'Pilih Tanggal Cupping',
    itemHasCuppingProfiles: 'Item ini memiliki {{count}} profil cupping. Pilih tanggal untuk melihat:',
    viewOtherProfiles: 'Lihat Profil Lain',
  },
  finishedGoodsProduction: {
    modal: {
      createDraft: 'Buat Draft Finished Goods',
      completeDraft: 'Selesaikan Draft Finished Goods',
      viewDraft: 'Lihat Draft Finished Goods',
      useExisting: 'Gunakan yang Ada',
      createNew: 'Buat Baru',
      finalQuantityPcs: 'Kuantitas Akhir (pcs)',
      calculateMaxQty: 'Hitung qty maks',
      completeRequiredToSave: 'Lengkapi field wajib untuk mengaktifkan Simpan.',
      processing: 'Memproses...',
      complete: 'Selesaikan',
      workOrderOnlyNotice: 'Hanya roasted bulk hasil work order yang telah selesai, atau yang di-import dengan sumber green bean, yang bisa dipilih sebagai komponen.',
      closeConfirmTitle: 'Tutup Form Finished Goods',
      closeConfirmMessage: 'Perubahan yang belum disimpan akan hilang. Apakah Anda ingin melanjutkan?',
      keepEditing: 'Lanjut mengedit',
      discardChanges: 'Buang perubahan',
    },
    errors: {
      componentRequired: 'Komponen roasted bulk wajib diisi.',
      allocationInvalid: 'Alokasi harus lengkap, total 100%, dan sesuai target pack size.',
      exceedsMaxQty: 'Kuantitas melebihi kapasitas maksimum ({{maxQuantity}}). Kurangi atau klik Hitung qty maks.',
      loadDataFailed: 'Gagal memuat data produksi finished goods',
      missingProductionId: 'Production id tidak ditemukan',
      loadDraftFailed: 'Gagal memuat draft produksi. Silakan coba lagi.',
    },
  },
  qcCupping: {
    modeSimplified: 'Cupping Sederhana',
    modeFull: 'Versi Lengkap (Form Deskriptif CVA)',
    scaleSimplified: 'Skala 1-3 (Overall 1-9)',
    scaleFull: 'Skala 1-15',
    flavorNotes: 'Catatan Rasa (Kotak CATA)',
    notes: 'Catatan (Kotak CATA)',
    mouthfeelNotes: 'Catatan Mouthfeel (Kotak CATA)',
  },
  batch: {
    list: {
      placeholder: 'Halo',
    },
  },
  cupping: {
    modes: {
      upgradeRequired: 'Upgrade ke tier basic untuk menggunakan mode ini',
    },
    formSections: {
      lockSection: 'Kunci Bagian',
      unlockSection: 'Buka Kunci Bagian',
      mode: {
        simplified: 'Sederhana',
        descriptive: 'Form Deskriptif',
        affective: 'Form Afektif',
      },
      labels: {
        fragranceIntensity: 'Intensitas Fragrance',
        aromaIntensity: 'Intensitas Aroma',
        flavor: 'Flavor',
        aftertaste: 'Aftertaste',
        mainTastes: 'Rasa Utama',
        max2: '(maks 2 opsi)',
        flavorAftertasteNotes: 'Catatan Flavor/Aftertaste',
        acidity: 'Acidity',
        intensityOnly: 'Hanya intensitas.',
        acidityNotes: 'Catatan Acidity',
        sweetness: 'Sweetness',
        sweetnessNotes: 'Catatan Sweetness',
        mouthfeel: 'Mouthfeel',
        attributes: 'Atribut',
        mouthfeelNotes: 'Catatan Mouthfeel',
        physicalCheckPassed: 'Pengecekan Fisik Lolos',
        yes: 'YA',
        no: 'TIDAK',
        defects: 'Defect',
        balance: 'Balance',
        overall: 'Overall',
        saveProfile: 'Simpan Profil',
      },
    },
    sca: {
      fragrance: 'Fragrance',
      aroma: 'Aroma',
      nonUniformCups: 'Jumlah Cangkir Non-Uniform',
      defectiveCups: 'Jumlah Cangkir Defect',
      defectsIfAny: 'Defect (jika ada)',
      score: 'Skor',
    },
    simplified: {
      dryAcidity: 'Dry Acidity (Herby, Grassy, Tart)',
      sweetAcidity: 'Sweet Acidity (Juicy, Fruit-like, Bright)',
      notBalance: 'Tidak seimbang',
      wellBalance: 'Sangat seimbang',
      roastDefects: 'Defect Roast',
      underdevelopment: 'Underdevelopment',
      overdevelopment: 'Overdevelopment',
      baked: 'Baked',
      scorched: 'Scorched',
    },
    formFields: {
      roastLevel: 'Level Roast',
      light: 'Light',
      medium: 'Medium',
      dark: 'Dark',
      low: 'Rendah',
      high: 'Tinggi',
    },
  },
  chatWidget: {
    welcome: 'Hai! Saya ERP Assistant Anda. Saya dapat membantu membuat order, cek inventory, atau menganalisis penjualan. Ada yang bisa saya bantu?',
    openAria: 'Buka AI Assistant',
    askAiAssistant: 'Tanya AI Assistant',
    erpAssistant: 'ERP Assistant',
    online: 'Online',
    typeMessage: 'Ketik pesan...',
    disclaimer: 'AI bisa melakukan kesalahan. Harap verifikasi informasi penting.',
  },
  notifications: {
    settings: {
      title: 'Pengingat Notifikasi',
      description: 'Atur berapa kali dan kapan pengingat dikirim sebelum roasting dimulai.',
      addReminder: 'Tambah pengingat',
      maxReached: 'Maksimum 5 pengingat',
      enableToggle: 'Kirim pengingat untuk work order baru',
      unitMinutes: 'menit',
      unitHours: 'jam',
      unitDays: 'hari',
      beforeStart: 'sebelum mulai',
      resyncButton: 'Sinkronkan ulang WO yang tertunda',
      saveSuccess: 'Pengaturan tersimpan',
    },
    bell: {
      title: 'Notifikasi',
      empty: 'Belum ada notifikasi',
      markAllRead: 'Tandai semua sudah dibaca',
    },
    toast: {
      newIncoming: 'Ada notifikasi baru masuk',
      workOrderReminder: {
        title: 'Roasting akan dimulai dalam {{offsetLabel}}',
        body: '{{woNumber}} · {{roastType}} · {{weight}} kg',
      },
    },
    offsetLabel: {
      minutes: 'menit',
      hours: 'jam',
      days: 'hari',
    },
  },
  roles: {
    page: {
      title: 'Peran & Izin',
      subtitle: 'Atur tindakan apa saja yang dapat dilakukan tiap peran di ruang kerja Anda.',
      newRole: 'Peran Baru',
      empty: 'Belum ada peran — buat satu untuk memulai.',
      upgradeTooltip: 'Tingkatkan paket untuk kustomisasi peran',
      upgradeBanner:
        'Paket Anda saat ini hanya dapat melihat peran. Tingkatkan paket untuk membuat atau mengubah peran.',
      sectionSystem: 'Peran Bawaan',
      sectionSystemHint:
        'Peran standar dengan akses penuh. Dapat ditinjau, namun tidak dapat diubah.',
      sectionCustom: 'Peran Kustom',
      sectionCustomHint:
        'Peran yang disesuaikan untuk tim Anda. Dapat diubah dan dihapus jika sudah tidak terpakai.',
      actions: 'Aksi',
    },
    badges: {
      system: 'Peran sistem',
      default: 'Bawaan',
    },
    table: {
      displayName: 'Peran',
      canonicalName: 'Nama Kanonik',
      memberCount: 'Anggota',
      createdAt: 'Dibuat',
    },
    delete: {
      title: 'Hapus peran?',
      confirmPrefix: 'Anda akan menghapus',
      hasMembers: 'Pindahkan {{count}} anggota sebelum menghapus',
      reassignFirst: 'Pindahkan anggota terlebih dahulu',
      failed: 'Gagal menghapus peran',
    },
    editor: {
      createTitle: 'Buat Peran',
      editTitle: 'Ubah Peran',
      viewTitle: 'Lihat Peran',
      displayName: 'Nama Peran',
      displayNamePlaceholder: 'cth. Supervisor Gudang',
      description: 'Deskripsi',
      descriptionPlaceholder: 'Apa tanggung jawab peran ini?',
      systemRoleNote:
        'Peran sistem memiliki akses penuh ke semua resource. Izinnya tidak dapat diubah.',
      upgradeBanner: 'Tingkatkan paket Anda untuk kustomisasi peran.',
      resetDefaults: 'Setel ulang ke bawaan',
      resetConfirmTitle: 'Setel ulang ke izin bawaan?',
      resetConfirmBody:
        'Tindakan ini mengganti seluruh izin saat ini dengan bawaan standar. Lanjutkan?',
      resetConfirm: 'Setel Ulang',
      saveFailed: 'Gagal menyimpan peran',
      resetFailed: 'Gagal menyetel ulang peran',
      permissionsTitle: 'Izin Akses',
      permissionsSubtitle:
        'Atur tindakan apa saja yang dapat dilakukan peran ini pada tiap resource.',
      searchPlaceholder: 'Cari resource…',
      noMatches: 'Tidak ada resource yang cocok.',
      selectedSummary: '{{selected}} dari {{total}} izin aktif',
      readOnlySummary: 'Total {{total}} izin',
    },
    grid: {
      resource: 'Resource',
      action: {
        read: 'Lihat',
        write: 'Ubah',
        delete: 'Hapus',
      },
      quick: 'Cepat',
      grantAll: 'Izinkan semua',
      clear: 'Kosongkan',
      unsupported: 'T/A',
    },
    categories: {
      operations: 'Operasional',
      profiles: 'Profil & Kualitas',
      sales: 'Penjualan & Pelanggan',
      team: 'Tim & Aset',
      intelligence: 'Analitik & Laporan',
      company: 'Perusahaan',
      other: 'Lainnya',
    },
    resources: {
      purchase_orders: 'Pesanan Pembelian',
      suppliers: 'Pemasok',
      inventory: 'Inventaris',
      green_bean_profiles: 'Profil Green Bean',
      taste_profiles: 'Profil Rasa',
      roast_profiles: 'Profil Sangrai',
      work_orders_roasting: 'Work Order — Roasting',
      work_orders_packing: 'Work Order — Packing',
      recipes: 'Resep',
      artisan_profiles: 'Profil Artisan',
      cupping_sessions: 'Sesi Cupping',
      sales_orders: 'Pesanan Penjualan',
      customers: 'Pelanggan',
      members: 'Anggota Tim',
      machines: 'Mesin',
      machine_types: 'Tipe Mesin',
      roasters: 'Sangraiwan',
      qora_assistant: 'Qora AI Assistant',
      dashboard_avg_shrinkage: 'Dashboard — Rata-rata Shrinkage',
      dashboard_outstanding_payment: 'Dashboard — Pembayaran Tertunda',
      dashboard_revenue: 'Dashboard — Pendapatan',
      dashboard_expenses: 'Dashboard — Biaya',
      dashboard_net_profit: 'Dashboard — Laba Bersih',
      dashboard_green_bean_value: 'Dashboard — Nilai Stok Green Bean',
      dashboard_roasted_value: 'Dashboard — Nilai Stok Roasted',
      dashboard_finished_value: 'Dashboard — Nilai Finished Goods',
      dashboard_species: 'Dashboard — Distribusi Spesies',
      dashboard_varietas: 'Dashboard — Distribusi Varietas',
      dashboard_recent_activities: 'Dashboard — Aktivitas Terbaru',
      dashboard_expiring_soon: 'Dashboard — Akan Kedaluwarsa',
      dashboard_low_stock: 'Dashboard — Stok Menipis',
      reports_roasting: 'Laporan — Roasting',
      reports_green_beans: 'Laporan — Green Beans',
      reports_roasted_beans: 'Laporan — Roasted Beans',
      reports_finished_goods: 'Laporan — Finished Goods',
      reports_business: 'Laporan — Bisnis',
      reports_customer: 'Laporan — Pelanggan',
      activity_logs: 'Log Aktivitas',
      company: 'Info Perusahaan',
    },
    resourceDescriptions: {
      purchase_orders: 'Buat, kirim, dan pantau pesanan ke pemasok.',
      suppliers: 'Direktori pemasok, kontak, dan riwayat transaksi.',
      inventory: 'Stok tersedia, lot, dan penyesuaian inventaris.',
      green_bean_profiles: 'Profil asal dan kualitas biji mentah.',
      taste_profiles: 'Catatan cicip dan profil rasa.',
      roast_profiles: 'Kurva sangrai dan profil tersimpan.',
      work_orders_roasting: 'Buat dan jadwalkan work order roasting.',
      work_orders_packing: 'Buat dan kelola draft produksi packing finished-good.',
      recipes: 'Definisi resep blend dan produk.',
      artisan_profiles: 'Profil ekspor logger Artisan.',
      cupping_sessions: 'Sesi cupping dan lembar penilaian.',
      sales_orders: 'Pesanan pelanggan dan fulfilment.',
      customers: 'Direktori pelanggan dan kontak.',
      members: 'Undang, reset, dan kelola anggota perusahaan.',
      machines: 'Mesin sangrai dan peralatan.',
      machine_types: 'Definisi tipe mesin.',
      roasters: 'Profil operator sangrai.',
      qora_assistant: 'Asisten AI percakapan Qora.',
      dashboard_avg_shrinkage: 'KPI rata-rata weight-loss roasting di dashboard.',
      dashboard_outstanding_payment: 'KPI nilai sales order yang belum dibayar di dashboard.',
      dashboard_revenue: 'KPI pendapatan di dashboard.',
      dashboard_expenses: 'KPI pengeluaran di dashboard.',
      dashboard_net_profit: 'KPI laba bersih di dashboard.',
      dashboard_green_bean_value: 'Valuasi stok green bean di dashboard.',
      dashboard_roasted_value: 'Valuasi stok roasted bean di dashboard.',
      dashboard_finished_value: 'Valuasi stok finished goods di dashboard.',
      dashboard_species: 'Infografik distribusi spesies di dashboard.',
      dashboard_varietas: 'Infografik distribusi varietas di dashboard.',
      dashboard_recent_activities: 'Feed aktivitas terbaru di dashboard.',
      dashboard_expiring_soon: 'Peringatan stok akan kedaluwarsa di dashboard.',
      dashboard_low_stock: 'Peringatan stok menipis di dashboard.',
      reports_roasting: 'Laporan batch roasting dan analitik weight-loss.',
      reports_green_beans: 'Laporan inventaris green bean dan pemasok.',
      reports_roasted_beans: 'Laporan inventaris roasted bean dan produksi.',
      reports_finished_goods: 'Laporan inventaris finished good dan produksi.',
      reports_business: 'Laporan pelanggan, pendapatan, dan kinerja shift.',
      reports_customer: 'Laporan riwayat pembelian dan ragam produk per pelanggan.',
      activity_logs: 'Audit log aktivitas pengguna dan sistem.',
      company: 'Profil, logo, dan preferensi perusahaan.',
    },
  },
  subscription: {
    banner: {
      free: 'Kamu di tier Gratis. Beberapa fitur lanjutan butuh upgrade.',
      warning: 'Langganan kamu berakhir dalam {{days}} hari.',
      cta_claim: 'Klaim Voucher',
      cta_renew: 'Perpanjang',
    },
    page_subtitle: 'Kelola tier langganan dan tukarkan kode voucher kamu.',
    tier_pro: 'Basic',
    tier_free: 'Gratis',
    tier: {
      free: 'Gratis',
      basic: 'Basic',
      plus: 'Plus',
      tooltip: {
        title: {
          basic: 'Langganan Basic',
          plus: 'Langganan Plus',
        },
        expiryLabel: 'Berakhir pada',
        daysLeft: '{{days}} hari tersisa',
        manage: 'Kelola langganan',
        upgradeNow: 'Upgrade sekarang',
        freeHint: 'Sedang di paket gratis',
      },
    },
    hero_pro_title: 'Akses penuh aktif',
    hero_pro_subtitle: 'Semua fitur lanjutan (mode cupping, integrasi, kuota roast lebih besar) terbuka.',
    hero_free_title: 'Kamu di paket Gratis',
    hero_free_subtitle: 'CRUD penuh di semua modul. Kuota roast 30 kg/bulan. Tukarkan voucher untuk upgrade.',
    usage: {
      title: 'Pemakaian roasting bulan ini',
      cycleLabel: '{{start}} – {{end}}  ·  Reset {{nextStart}}',
      usedOf: '{{used}} / {{limit}} kg',
      remaining: 'Sisa: {{remaining}} kg',
      upgradeForMore: 'Upgrade untuk lebih banyak →',
    },
    errors: {
      quotaExceeded: 'Kamu sudah pakai {{used}}/{{limit}} kg bulan ini. Reset pada {{date}}.',
    },
    quotaModal: {
      title: 'Kuota roasting tercapai',
      eyebrow: 'Perhatian',
      bodyFree: 'Oops! Pemakaian work order bulan ini sudah melampaui kuota. Upgrade ke Basic untuk lanjut roasting.',
      bodyBasic: 'Oops! Pemakaian work order bulan ini sudah melampaui kuota. Upgrade ke Plus untuk lanjut roasting.',
      bodyPlus: 'Kuota Plus untuk siklus ini sudah habis. Siklus baru dimulai {{date}} — ini sudah tier tertinggi yang kami tawarkan saat ini.',
      cycleUsage: 'Siklus ini',
      usage: '{{used}} / {{limit}} kg terpakai',
      attempted: 'untuk work order ini',
      resetsOn: 'Reset {{date}}',
      nextTierLine: {
        basic: 'Basic — 250 kg / bulan',
        plus: 'Plus — 1.000 kg / bulan',
      },
      cta: {
        upgradeToBasic: 'Upgrade ke Basic',
        upgradeToPlus: 'Upgrade ke Plus',
        dismiss: 'Tutup',
      },
    },
    machineTypeQuota: {
      title: 'Pemakaian tipe mesin',
      usedOf: '{used} / {limit} tipe',
      activeOf: '{active} aktif',
      unlimited: 'Tanpa batas',
      remaining: 'Sisa: {remaining}',
      upgradeForMore: 'Upgrade untuk lebih banyak →',
      eyebrow: 'Tipe mesin',
    },
    machineTypeLimitModal: {
      title: 'Batas tipe mesin tercapai',
      eyebrow: 'Batas langganan',
      bodyFree:
        'Anda sudah mencapai batas tipe mesin tier gratis ({used}/{limit}). Batas ini menghitung semua tipe — aktif maupun tidak — jadi Anda tidak bisa menambah tipe baru tanpa menghapus yang ada. Upgrade ke Basic untuk hingga 2 tipe.',
      bodyBasic:
        'Anda sudah mencapai batas tier Basic ({used}/{limit}). Batas ini menghitung semua tipe — aktif maupun tidak. Upgrade ke Plus untuk tipe tanpa batas.',
      reactivateNote:
        'Tipe yang ada tetap tersimpan. Hapus satu untuk membuka slot, atau upgrade.',
      cta: {
        upgradeToBasic: 'Upgrade ke Basic',
        upgradeToPlus: 'Upgrade ke Plus',
        dismiss: 'Tutup',
      },
    },
    upgradeRequired: {
      artisanIntegration: {
        title: 'Perlu upgrade',
        body: 'Integrasi Artisan memerlukan tier Basic atau lebih tinggi. Upgrade untuk membuat API key.',
        cta: 'Buka Langganan',
      },
    },
    days_remaining: 'sisa {{days}} hari',
    current_tier: 'Tier Saat Ini',
    expires_on: 'Berlaku hingga',
    buy: {
      upgrade_to_basic: 'Upgrade ke Basic',
      upgrade_to_plus: 'Upgrade ke Plus',
    },
    claim_title: 'Klaim Voucher',
    claim_subtitle: 'Masukkan kode dari admin kamu. Sisa masa aktif akan ditambahkan dari masa berlaku saat ini.',
    code_placeholder: 'XXXX-XXXX-XXXX',
    redeem_cta: 'Tukarkan',
    history_title: 'Riwayat Penukaran',
    history_subtitle: 'Semua voucher yang pernah ditukarkan oleh perusahaan ini.',
    history_empty: 'Belum ada voucher yang ditukarkan.',
    history: {
      redeemed_at: 'Diklaim',
      months_added: 'Bulan',
      extended_to: 'Berakhir Baru',
    },
    redeem: {
      success: 'Aktif! +{{months}} bulan dari {{name}}.',
      error_generic: 'Voucher tidak dapat ditukarkan. Periksa kode dan coba lagi.',
    },
    upgrade_to_use: 'Upgrade ke Basic untuk pakai fitur ini',
    voucher: {
      page_subtitle: 'Terbitkan, audit, dan kelola voucher langganan untuk seluruh platform.',
      search_placeholder: 'Cari berdasarkan kode atau nama…',
      stats: {
        total: 'Total',
        active: 'Aktif',
        expired: 'Kedaluwarsa',
        exhausted: 'Habis',
      },
      create_title: 'Voucher Baru',
      create_cta: 'Voucher Baru',
      list_empty: 'Tidak ada voucher ditemukan.',
      audit_title: 'Penukaran',
      audit_empty: 'Belum ada penukaran.',
      audit_count: '{{count}} penukaran',
      delete_title: 'Hapus voucher?',
      delete_description: 'Kode {{code}} tidak akan bisa ditukarkan lagi. Penukaran yang sudah terjadi tetap berlaku.',
      create_success: 'Voucher berhasil dibuat',
      create_error: 'Gagal membuat voucher',
      delete_success: 'Voucher berhasil dihapus',
      delete_error: 'Gagal menghapus voucher',
      confirm_delete: 'Hapus lunak voucher ini? Penukaran yang sudah terjadi tetap berlaku.',
      unlimited: 'Tak terbatas',
      filter: {
        all: 'Semua',
        active: 'Aktif',
        expired: 'Kedaluwarsa',
        exhausted: 'Habis',
        deleted: 'Dihapus',
      },
      status: {
        active: 'Aktif',
        expired: 'Kedaluwarsa',
        exhausted: 'Habis',
        deleted: 'Dihapus',
      },
      col: {
        code: 'Kode',
        name: 'Nama',
        duration: 'Durasi',
        expires: 'Kedaluwarsa',
        usage: 'Penggunaan',
        status: 'Status',
      },
      field: {
        name: 'Nama voucher',
        code_optional: 'Kode (opsional — dibuat otomatis jika kosong)',
        months: 'Bulan yang diberikan',
        max_redemptions: 'Maks penukaran (kosong = tidak terbatas)',
      },
    },
    admin: {
      page_subtitle: 'Tinjau tier, masa berlaku, dan lakukan penyesuaian manual per perusahaan.',
      search_placeholder: 'Cari perusahaan…',
      tier_filter: {
        all: 'Semua',
        free: 'Free',
        basic: 'Basic',
        plus: 'Plus',
      },
      stats: {
        total: 'Perusahaan',
        free: 'Tier Free',
        basic: 'Tier Basic',
        plus: 'Tier Plus',
        expiring: 'Habis ≤7 hari',
      },
      empty: 'Tidak ada perusahaan yang cocok.',
      col: {
        company: 'Perusahaan',
        tier: 'Tier',
        expires: 'Kedaluwarsa',
      },
      adjust_cta: 'Sesuaikan',
      adjust_title: 'Sesuaikan langganan',
      field: {
        months_delta: 'Delta bulan (positif memperpanjang, negatif mempersingkat)',
        tier_unchanged: 'Tier — biarkan apa adanya',
        reason: 'Alasan (wajib untuk audit)',
        adjustment_type: 'Penyesuaian',
        adjustment_none: 'Tidak ada perubahan bulan',
        adjustment_add: 'Tambah bulan',
        adjustment_reduce: 'Kurangi bulan',
        months_amount: 'Jumlah bulan',
      },
      validation: {
        nothing_changed: 'Berikan perubahan bulan, tier baru, atau keduanya.',
        months_positive: 'Masukkan jumlah bulan positif.',
      },
      confirm: {
        title: 'Konfirmasi penyesuaian',
        intro: 'Mohon tinjau perubahan di bawah. Tindakan ini dicatat untuk audit.',
        company: 'Perusahaan',
        current_tier: 'Tier saat ini',
        new_tier: 'Tier baru',
        tier_unchanged: 'Tidak berubah',
        months_change: 'Masa langganan',
        months_extend: 'Perpanjang {{count}} bulan',
        months_shorten: 'Persingkat {{count}} bulan',
        months_none: 'Tidak ada perubahan',
        expires_current: 'Kedaluwarsa saat ini',
        expires_new: 'Kedaluwarsa baru (perkiraan)',
        reason: 'Alasan',
        back: 'Kembali',
        confirm_cta: 'Konfirmasi & simpan',
      },
    },
  },
  labels: {
    downloadLabel: 'Download Label',
  },
};
